import React, { useEffect, useMemo, useRef, useState } from "react";
import { cargarCentrosClientes } from "../controllers/centrosControllers";
import {
    obtenerEquipos,
    obtenerActasEntrega,
    crearActaEntrega,
    actualizarActaEntrega,
    eliminarActaEntrega,
    obtenerArmados,
    obtenerMaterialesArmado,
    obtenerPermisosTrabajo,
    obtenerMantencionesTerreno,
    crearMantencionTerreno,
    actualizarMantencionTerreno,
    obtenerRetirosTerreno,
    updateRetiroTerreno,
    obtenerLevantamientosTerreno,
    solicitarEdicionRetiroTerreno,
    resolverEdicionLevantamientoTerreno,
    resolverEdicionRetiroTerreno,
    obtenerCambiosEquipoMantencion,
    crearPermisoTrabajo,
    actualizarPermisoTrabajo,
    eliminarPermisoTrabajo,
    eliminarMantencionTerreno,
    eliminarRetiroTerreno,
    eliminarLevantamientoTerreno
} from "../api";
import "./InformesCentros.css";

const SUBCATEGORIAS = [
    { value: "acta_entrega", label: "Actas de entrega" },
    { value: "reapuntamiento", label: "Reapuntamiento" },
    { value: "intervencion", label: "Permisos de trabajo" },
    { value: "mantencion", label: "Mantenciones" },
    { value: "retiro", label: "Retiros" },
    { value: "levantamiento", label: "Levantamientos" }
];


const toInputDate = (value) => {
    if (!value) return "";
    const raw = String(value).trim();
    const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
    const latam = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (latam) return `${latam[3]}-${latam[2]}-${latam[1]}`;
    const dt = new Date(raw);
    if (!Number.isNaN(dt.getTime())) {
        const y = dt.getFullYear();
        const m = String(dt.getMonth() + 1).padStart(2, "0");
        const d = String(dt.getDate()).padStart(2, "0");
        return `${y}-${m}-${d}`;
    }
    return "";
};

const toDisplayDate = (value) => {
    const iso = toInputDate(value);
    if (!iso) return "-";
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
};

const toDisplayHour = (value) => {
    if (!value) return "-";
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return "-";
    const hh = String(dt.getHours()).padStart(2, "0");
    const mm = String(dt.getMinutes()).padStart(2, "0");
    return `${hh}:${mm}hrs`;
};

const formatMeasureWithUnit = (value, unit) => {
    const raw = String(value ?? "").trim();
    if (!raw) return "-";
    const normalized = raw.toLowerCase();
    if (normalized.includes(unit.toLowerCase())) return raw;
    return `${raw}${unit.toLowerCase()}`;
};

const getLevantamientoFotos = (value) => {
    if (Array.isArray(value)) return value;
    if (!value) return [];
    try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
        return [];
    }
};

const normalizeLevantamientoEstado = (value) => String(value || "").trim().toLowerCase();

const renderLevantamientoEstadoBadge = (estadoRaw) => {
    const estado = normalizeLevantamientoEstado(estadoRaw);
    if (estado === "edicion_solicitada") return <span className="badge badge-warning">Edicion solicitada</span>;
    if (estado === "edicion_autorizada") return <span className="badge badge-info">Edicion autorizada</span>;
    if (estado === "edicion_rechazada") return <span className="badge badge-danger">Edicion rechazada</span>;
    if (estado === "finalizado") return <span className="badge badge-success">Finalizado</span>;
    return <span className="badge badge-secondary">{estadoRaw || "-"}</span>;
};

const todayLocalInputDate = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const isSignatureImage = (value) => {
    return /^data:image\/[a-zA-Z0-9+.-]+;base64,/.test(String(value || "").trim());
};

const esc = (v) =>
    String(v ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");

const signatureHtml = (value, label = "Sin firma") => {
    if (!value) return `<div class="sig-empty">${esc(label)}</div>`;
    if (isSignatureImage(value)) {
        return `<img src="${value}" alt="Firma" class="sig-img" />`;
    }
    return `<div class="sig-text">${esc(value)}</div>`;
};

const normalizeBoolSelectValue = (value) => {
    if (value === true) return "si";
    if (value === false) return "no";
    const text = String(value || "").trim().toLowerCase();
    if (!text) return "";
    if (["si", "sí", "true", "1", "yes", "y"].includes(text)) return "si";
    if (["no", "false", "0", "n"].includes(text)) return "no";
    return "";
};

const getFirmasTecnicosAdicionales = (value) => {
    if (Array.isArray(value)) return value.filter((item) => item && typeof item === "object");
    if (!value) return [];
    try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed.filter((item) => item && typeof item === "object") : [];
    } catch (error) {
        return [];
    }
};

const getTecnicosAdicionalesActa = (acta) => {
    const base = new Set(
        [acta?.tecnico_1, acta?.tecnico_2]
            .map((item) => String(item || "").trim())
            .filter(Boolean)
    );
    return getFirmasTecnicosAdicionales(acta?.firmas_tecnicos_adicionales)
        .map((item) => ({
            nombre: String(item?.nombre || "").trim(),
            firma: item?.firma || ""
        }))
        .filter((item) => item.nombre && !base.has(item.nombre));
};

const evidenceHtml = (value) => {
    const raw = String(value || "").trim();
    if (!raw) return `<div class="sig-empty">Sin evidencia</div>`;
    let items = [];
    try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
            items = parsed.map((v) => String(v || "").trim()).filter(Boolean);
        }
    } catch (_) {
        items = [];
    }
    if (!items.length) items = [raw];
    const rendered = items.slice(0, 3).map((src, idx) => {
        if (/^data:image\/[a-zA-Z0-9+.-]+;base64,/.test(src) || /^https?:\/\//i.test(src)) {
            return `<div class="evi-item"><img src="${esc(src)}" alt="Evidencia ${idx + 1}" class="evi-img" /></div>`;
        }
        return `<div class="evi-item"><div class="sig-text">${esc(src)}</div></div>`;
    });
    return `<div class="evi-grid">${rendered.join("")}</div>`;
};

const parseGpsPoints = (value) => {
    const raw = String(value || "");
    if (!raw.trim()) return [{ lat: "", lng: "" }];
    const parts = raw
        .split("|")
        .map((p) => p.trim())
        .filter(Boolean);
    const points = parts
        .map((p) => {
            const [latRaw = "", lngRaw = ""] = p.split(",");
            return { lat: latRaw.trim(), lng: lngRaw.trim() };
        })
        .filter((p) => p.lat || p.lng);
    return points.length ? points : [{ lat: "", lng: "" }];
};

const parseSellos = (value) => {
    const raw = String(value || "").trim();
    if (!raw) return [{ ubicacion: "", numeroAnterior: "", numeroNuevo: "" }];
    try {
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [{ ubicacion: "", numeroAnterior: "", numeroNuevo: "" }];
        const items = parsed
            .map((item) => ({
                ubicacion: String(item?.ubicacion || "").trim(),
                numeroAnterior: String(item?.numero_anterior || item?.numero || "").trim(),
                numeroNuevo: String(item?.numero_nuevo || "").trim()
            }))
            .filter((item) => item.ubicacion || item.numeroAnterior || item.numeroNuevo);
        return items.length ? items : [{ ubicacion: "", numeroAnterior: "", numeroNuevo: "" }];
    } catch (error) {
        return [{ ubicacion: "", numeroAnterior: "", numeroNuevo: "" }];
    }
};

const normalizeMeasureInput = (value) => {
    let text = String(value ?? "").replace(/,/g, ".").replace(/[^\d.]/g, "");
    const firstDot = text.indexOf(".");
    if (firstDot !== -1) {
        text = text.slice(0, firstDot + 1) + text.slice(firstDot + 1).replace(/\./g, "");
    }
    return text;
};

const normalizeSelloNumero = (value) => String(value || "").replace(/\D/g, "");

const parseEvidencePhotos = (value) => {
    const raw = String(value || "").trim();
    if (!raw) return [];
    try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
            return parsed.map((item) => String(item || "").trim()).filter(Boolean).slice(0, 3);
        }
    } catch (error) {
        return [raw];
    }
    return [raw];
};

const parseChecklistEquipos = (value) => {
    if (!value) return [];
    try {
        const parsed = Array.isArray(value) ? value : JSON.parse(String(value));
        if (!Array.isArray(parsed)) return [];
        return parsed
            .map((item) => ({
                equipo_id: Number(item?.equipo_id || 0) || null,
                equipo_nombre: String(item?.equipo_nombre || "").trim(),
                numero_serie: String(item?.numero_serie || "").trim(),
                codigo: String(item?.codigo || "").trim(),
                revisado: !!item?.revisado,
                observacion: String(item?.observacion || "").trim()
            }))
            .filter((item) => item.equipo_nombre || item.numero_serie || item.codigo);
    } catch (error) {
        return [];
    }
};

const normalizeGpsPointInput = (value) => {
    let text = String(value ?? "").replace(/[^\d.\-]/g, "");
    const minusCount = (text.match(/-/g) || []).length;
    if (minusCount > 1) text = `-${text.replace(/-/g, "")}`;
    if (text.includes("-") && !text.startsWith("-")) text = `-${text.replace(/-/g, "")}`;
    const firstDot = text.indexOf(".");
    if (firstDot !== -1) {
        text = text.slice(0, firstDot + 1) + text.slice(firstDot + 1).replace(/\./g, "");
    }
    if (text && !text.startsWith("-")) text = `-${text}`;
    return text;
};
const normalizeText = (value = "") =>
    String(value || "")
        .trim()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
const normalizeRetiroModalidad = (value = "") => {
    const normalized = normalizeText(value);
    return normalized === "por_mano" ? "por_mano" : "despacho_orca";
};
const getRetiroEstadoPayload = (equipos = []) => {
    const retirados = (Array.isArray(equipos) ? equipos : []).filter((item) => !!item?.retirado);
    if (!retirados.length) return "retirado_centro";
    return retirados.some((item) => normalizeRetiroModalidad(item?.modalidad_retorno) === "despacho_orca")
        ? "en_transito"
        : "retirado_centro";
};
const normalizeCorrelativoSearch = (value = "") =>
    String(value || "")
        .trim()
        .toLowerCase()
        .replace(/^n\s*/i, "")
        .replace(/[^\d]/g, "");

const OVERVIEW_PERIODS = [
    { value: "7d", label: "7 dias" },
    { value: "30d", label: "30 dias" },
    { value: "month", label: "Este mes" },
    { value: "all", label: "Todos" }
];

const startOfToday = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

const startOfCurrentMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
};

const parseComparableDate = (value) => {
    const iso = toInputDate(value);
    if (iso) {
        const [y, m, d] = iso.split("-").map(Number);
        return new Date(y, (m || 1) - 1, d || 1);
    }
    if (!value) return null;
    const dt = new Date(value);
    return Number.isNaN(dt.getTime()) ? null : dt;
};

const SINONIMOS_EQUIPOS = {
    "ip pc": "pc",
    "ip pc nvr": "pc",
    "puerta de enlace": "router",
    "router (puerta de enlace)": "router",
    "mastil": "mastil",
    "mástil": "mastil",
    "switch cisco + adaptador": "switch (cisco)",
    "switch cisco": "switch (cisco)"
};

const GRUPOS_EQUIPOS_ARMADO = [
    {
        titulo: "Oficina",
        items: [
            "PC",
            "Monitor",
            "Mouse",
            "Teclado",
            "Router",
            "Switch",
            "Switch (Cisco)",
            "Switch raqueable",
            "Camara Interior",
            "Parlantes",
            "Sensor Magnetico",
            "Rack 9U - tuercas - tornillos",
            "Bandeja Rack - tornillos",
            "Zapatilla Rack (PDU)"
        ]
    },
    {
        titulo: "Tablero Alarma",
        items: [
            "Tablero 500x400x200",
            "Baliza Interior",
            "Bocina Interior",
            "Baliza Exterior 1",
            "Baliza Exterior 2",
            "Bocina Exterior 1",
            "Bocina Exterior 2",
            "Foco led 1 150W",
            "Foco led 2 150W",
            "Foco led 1 50W",
            "Foco led 2 50W",
            "Fuente poder 12V",
            "Axis P8221"
        ]
    },
    {
        titulo: "Tablero Respaldo",
        items: [
            "Tablero 1200x800x300",
            "Tablero 1000x600x300",
            "Inversor cargador Victron",
            "Panel Victron",
            "Bateria 1",
            "Bateria 2",
            "Bateria 3",
            "Bateria 4",
            "Bateria 5",
            "Bateria 6",
            "UPS online",
            "Switch POE",
            "Sensor magnetico respaldo",
            "Sensor magnetico cargador",
            "Cargador 1",
            "Cargador 2",
            "Tablero Cargador 750x500x250"
        ]
    },
    {
        titulo: "Mastil",
        items: [
            "Tablero Derivacion (400x300x200)",
            "Radar 1",
            "Radar 2",
            "Cable rj radar 1",
            "Cable rj radar 2",
            "Soporte radar 1",
            "Soporte radar 2",
            "Camara PTZ termal",
            "Camara PTZ Laser",
            "Camara PTZ Laser 2",
            "Camara Modulo",
            "Camara Silo 1",
            "Camara Silo 2",
            "Camara Ensinerador",
            "Ensilaje interior",
            "Ensilaje exterior",
            "Camara Popa",
            "Camara acceso 1",
            "Camara acceso 2",
            "Camara acceso 3",
            "Camara acceso 4",
            "Enlace Ubiquiti"
        ]
    },
    {
        titulo: "Tablero Camara",
        items: [
            "Tablero Camara (500x700x250)",
            "Poe Power 1",
            "Poe Power 2",
            "Poe Power 3",
            "Poe Power 4",
            "Poe Power 5",
            "Switch POE 1",
            "Switch POE 2",
            "Mass",
            "Tablero 750x500x250",
            "Switch 1",
            "Switch 2",
            "Switch 3",
            "Switch 4",
            "Netio"
        ]
    }
];

const normalizarNombreEquipo = (nombre = "") => {
    let n = String(nombre || "")
        .toLowerCase()
        .trim()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
    if (SINONIMOS_EQUIPOS[n]) n = SINONIMOS_EQUIPOS[n];
    return n;
};

function InformesCentros() {
    const [centros, setCentros] = useState([]);
    const [loadingCentros, setLoadingCentros] = useState(true);
    const [loadingActas, setLoadingActas] = useState(false);
    const [savingActa, setSavingActa] = useState(false);
    const [subcategoria, setSubcategoria] = useState("acta_entrega");
    const [mostrarEditorActa, setMostrarEditorActa] = useState(false);
    const [mostrarEditorPermiso, setMostrarEditorPermiso] = useState(false);

    // filtros de listado actas
    const [filtroCentroId, setFiltroCentroId] = useState("");
    const [filtroCliente, setFiltroCliente] = useState("all");
    const [filtroFechaDesde, setFiltroFechaDesde] = useState("");
    const [filtroFechaHasta, setFiltroFechaHasta] = useState("");
    const [filtroCorrelativo, setFiltroCorrelativo] = useState("");

    // formulario acta de entrega
    const [editandoId, setEditandoId] = useState(null);
    const [clienteIdForm, setClienteIdForm] = useState("");
    const [centroIdForm, setCentroIdForm] = useState("");
    const [fechaRegistro, setFechaRegistro] = useState("");
    const [region, setRegion] = useState("");
    const [localidad, setLocalidad] = useState("");
    const [tecnico1, setTecnico1] = useState("");
    const [firmaTecnico1, setFirmaTecnico1] = useState("");
    const [tecnico2, setTecnico2] = useState("");
    const [firmaTecnico2, setFirmaTecnico2] = useState("");
    const [tecnicosAdicionalesActa, setTecnicosAdicionalesActa] = useState([]);
    const [recepcionaNombre, setRecepcionaNombre] = useState("");
    const [firmaRecepciona, setFirmaRecepciona] = useState("");
    const [equiposConsiderados, setEquiposConsiderados] = useState("");
    const [centroOrigenTraslado, setCentroOrigenTraslado] = useState("");

    const [actasEntrega, setActasEntrega] = useState([]);
    const [permisosTrabajo, setPermisosTrabajo] = useState([]);
    const [overviewLoading, setOverviewLoading] = useState(false);
    const [overviewPeriodo, setOverviewPeriodo] = useState("30d");
    const [overviewActasEntrega, setOverviewActasEntrega] = useState([]);
    const [overviewReapuntamientos, setOverviewReapuntamientos] = useState([]);
    const [overviewPermisos, setOverviewPermisos] = useState([]);
    const [overviewMantenciones, setOverviewMantenciones] = useState([]);
    const [overviewRetiros, setOverviewRetiros] = useState([]);
    const [overviewLevantamientos, setOverviewLevantamientos] = useState([]);
    const [savingPermiso, setSavingPermiso] = useState(false);
    const [permisoEditandoId, setPermisoEditandoId] = useState(null);
    const [permisoActaEntregaId, setPermisoActaEntregaId] = useState(null);
    const [permisoClienteIdForm, setPermisoClienteIdForm] = useState("");
    const [permisoCentroIdForm, setPermisoCentroIdForm] = useState("");
    const [permisoFechaIngreso, setPermisoFechaIngreso] = useState("");
    const [permisoFechaSalida, setPermisoFechaSalida] = useState("");
    const [permisoCorreoCentro, setPermisoCorreoCentro] = useState("");
    const [permisoTelefonoCentro, setPermisoTelefonoCentro] = useState("");
    const [permisoRegion, setPermisoRegion] = useState("");
    const [permisoLocalidad, setPermisoLocalidad] = useState("");
    const [permisoResponsabilidad, setPermisoResponsabilidad] = useState("");
    const [permisoBaseTierra, setPermisoBaseTierra] = useState("");
    const [permisoCantidadRadares, setPermisoCantidadRadares] = useState("");
    const [permisoTecnico1, setPermisoTecnico1] = useState("");
    const [permisoFirmaTecnico1, setPermisoFirmaTecnico1] = useState("");
    const [permisoTecnico2, setPermisoTecnico2] = useState("");
    const [permisoFirmaTecnico2, setPermisoFirmaTecnico2] = useState("");
    const [permisoTecnicosAdicionales, setPermisoTecnicosAdicionales] = useState([]);
    const [permisoRecepcionaNombre, setPermisoRecepcionaNombre] = useState("");
    const [permisoRecepcionaRut, setPermisoRecepcionaRut] = useState("");
    const [permisoFirmaRecepciona, setPermisoFirmaRecepciona] = useState("");
    const [permisoPuntosGps, setPermisoPuntosGps] = useState([{ lat: "", lng: "" }]);
    const [permisoSellos, setPermisoSellos] = useState([{ ubicacion: "", numeroAnterior: "", numeroNuevo: "" }]);
    const [permisoArmadoEquipos, setPermisoArmadoEquipos] = useState([]);
    const [permisoMedicionFaseNeutro, setPermisoMedicionFaseNeutro] = useState("");
    const [permisoMedicionNeutroTierra, setPermisoMedicionNeutroTierra] = useState("");
    const [permisoHertz, setPermisoHertz] = useState("");
    const [permisoDescripcionTrabajo, setPermisoDescripcionTrabajo] = useState("");
    const [permisoChecklistEquiposRaw, setPermisoChecklistEquiposRaw] = useState("");
    const [permisoEvidencias, setPermisoEvidencias] = useState([]);
    const [permisoEvidenciaTargetIndex, setPermisoEvidenciaTargetIndex] = useState(null);
    const [permisoCambiosEquipo, setPermisoCambiosEquipo] = useState([]);
    const [retiroTipo, setRetiroTipo] = useState("parcial");
    const [retiroEquiposChecklist, setRetiroEquiposChecklist] = useState([]);
    const permisoEvidenciaInputRef = useRef(null);
    const [actaPage, setActaPage] = useState(1);
    const [permisoPage, setPermisoPage] = useState(1);
    const [actaPageSize, setActaPageSize] = useState(10);
    const [permisoPageSize, setPermisoPageSize] = useState(10);
    const [mostrarDetallePermiso, setMostrarDetallePermiso] = useState(false);
    const [detallePermisoTab, setDetallePermisoTab] = useState("gps");
    const [levantamientoDetalleId, setLevantamientoDetalleId] = useState(null);

    const centrosOrdenados = useMemo(
        () => [...centros].sort((a, b) => String(a.nombre || "").localeCompare(String(b.nombre || ""))),
        [centros]
    );

    const clientes = useMemo(() => {
        const set = new Set();
        centros.forEach((centro) => {
            if (centro?.cliente) set.add(centro.cliente);
        });
        return Array.from(set).sort((a, b) => a.localeCompare(b));
    }, [centros]);

    const centroSeleccionadoForm = useMemo(
        () => centros.find((c) => String(c.id || c.id_centro) === String(centroIdForm)),
        [centros, centroIdForm]
    );

    const getCentroClienteKey = (centro) => {
        if (!centro) return "";
        if (centro.cliente_id != null && centro.cliente_id !== "") return `id:${centro.cliente_id}`;
        if (centro.id_cliente != null && centro.id_cliente !== "") return `id:${centro.id_cliente}`;
        return `name:${String(centro.cliente || "").trim()}`;
    };

    const clientesForm = useMemo(() => {
        const map = new Map();
        centros.forEach((centro) => {
            const key = getCentroClienteKey(centro);
            if (!key) return;
            if (!map.has(key)) {
                map.set(key, { key, label: String(centro.cliente || "Cliente") });
            }
        });
        return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
    }, [centros]);

    const centrosFormFiltrados = useMemo(() => {
        if (!clienteIdForm) return centrosOrdenados;
        return centrosOrdenados.filter((centro) => getCentroClienteKey(centro) === clienteIdForm);
    }, [centrosOrdenados, clienteIdForm]);

    const centrosFiltroCliente = useMemo(() => {
        if (filtroCliente === "all") return centrosOrdenados;
        const clienteNorm = normalizeText(filtroCliente);
        return centrosOrdenados.filter((centro) => normalizeText(centro?.cliente) === clienteNorm);
    }, [centrosOrdenados, filtroCliente]);

    const actasFiltradas = useMemo(() => {
        return actasEntrega.filter((acta) => {
            const tipoActa = String(acta.tipo_instalacion || "instalacion").toLowerCase();
            if (subcategoria === "acta_entrega" && tipoActa !== "instalacion") return false;
            if (subcategoria === "reapuntamiento" && tipoActa !== "reapuntamiento") return false;
            if (filtroCliente !== "all") {
                const clienteActa = normalizeText(acta.cliente || acta.empresa || "");
                if (clienteActa !== normalizeText(filtroCliente)) return false;
            }
            if (filtroCentroId && String(acta.centro_id || "") !== String(filtroCentroId)) return false;
            const fecha = toInputDate(acta.fecha_registro);
            if (filtroFechaDesde && (!fecha || fecha < filtroFechaDesde)) return false;
            if (filtroFechaHasta && (!fecha || fecha > filtroFechaHasta)) return false;
            return true;
        });
    }, [actasEntrega, filtroCliente, filtroCentroId, filtroFechaDesde, filtroFechaHasta, subcategoria]);

    const permisosFiltrados = useMemo(() => {
        const correlativoFiltro = normalizeCorrelativoSearch(filtroCorrelativo);
        return permisosTrabajo.filter((permiso) => {
            if (filtroCliente !== "all") {
                const clientePermiso = normalizeText(permiso.cliente || permiso.empresa || "");
                if (clientePermiso !== normalizeText(filtroCliente)) return false;
            }
            if (filtroCentroId && String(permiso.centro_id || "") !== String(filtroCentroId)) return false;
            const fecha = toInputDate(
                subcategoria === "retiro"
                    ? permiso.fecha_retiro
                    : subcategoria === "levantamiento"
                        ? permiso.fecha_levantamiento
                        : permiso.fecha_ingreso
            );
            if (filtroFechaDesde && (!fecha || fecha < filtroFechaDesde)) return false;
            if (filtroFechaHasta && (!fecha || fecha > filtroFechaHasta)) return false;
            if (correlativoFiltro) {
                const correlativoActual = subcategoria === "mantencion"
                    ? String(permiso.id_mantencion_terreno || "")
                    : subcategoria === "retiro"
                        ? String(permiso.id_retiro_terreno || "")
                        : subcategoria === "levantamiento"
                            ? String(permiso.id_levantamiento_terreno || "")
                            : String(permiso.id_permiso_trabajo || "");
                if (!correlativoActual.includes(correlativoFiltro)) return false;
            }
            return true;
        });
    }, [permisosTrabajo, filtroCliente, filtroCentroId, filtroFechaDesde, filtroFechaHasta, filtroCorrelativo, subcategoria]);

    const overviewRowsBase = useMemo(() => {
        const pushRow = (target, item) => target.push(item);
        const clienteMatch = (rawCliente) =>
            filtroCliente === "all" || normalizeText(rawCliente || "") === normalizeText(filtroCliente);

        const rows = [];

        overviewActasEntrega.forEach((acta) => {
            const cliente = acta.empresa || acta.cliente || "";
            if (!clienteMatch(cliente)) return;
            const fechaBase = acta.created_at || acta.updated_at || acta.fecha_registro;
            pushRow(rows, {
                id: `acta-${acta.id_acta_entrega}`,
                tipo: "acta_entrega",
                tipoLabel: "Instalacion",
                correlativo: `N${acta.id_acta_entrega || "-"}`,
                cliente,
                centro: acta.centro || "-",
                tecnico: [acta.tecnico_1, acta.tecnico_2, ...getTecnicosAdicionalesActa(acta).map((item) => item.nombre)].filter(Boolean).join(", ") || "-",
                estado: "Acta registrada",
                descripcion: acta.equipos_considerados || acta.codigo_ponton || "-",
                fechaPrincipal: acta.fecha_registro,
                fechaOrden: parseComparableDate(fechaBase || acta.fecha_registro),
                raw: acta
            });
        });

        overviewReapuntamientos.forEach((acta) => {
            const cliente = acta.empresa || acta.cliente || "";
            if (!clienteMatch(cliente)) return;
            const fechaBase = acta.created_at || acta.updated_at || acta.fecha_registro;
            pushRow(rows, {
                id: `reap-${acta.id_acta_entrega}`,
                tipo: "reapuntamiento",
                tipoLabel: "Reapuntamiento",
                correlativo: `N${acta.id_acta_entrega || "-"}`,
                cliente,
                centro: acta.centro || "-",
                tecnico: [acta.tecnico_1, acta.tecnico_2, ...getTecnicosAdicionalesActa(acta).map((item) => item.nombre)].filter(Boolean).join(", ") || "-",
                estado: "Acta registrada",
                descripcion: acta.equipos_considerados || acta.codigo_ponton || "-",
                fechaPrincipal: acta.fecha_registro,
                fechaOrden: parseComparableDate(fechaBase || acta.fecha_registro),
                raw: acta
            });
        });

        overviewPermisos.forEach((permiso) => {
            const cliente = permiso.empresa || permiso.cliente || "";
            if (!clienteMatch(cliente)) return;
            const fechaBase = permiso.created_at || permiso.updated_at || permiso.fecha_ingreso;
            pushRow(rows, {
                id: `perm-${permiso.id_permiso_trabajo}`,
                tipo: "intervencion",
                tipoLabel: "Permiso",
                correlativo: `N${permiso.id_permiso_trabajo || "-"}`,
                cliente,
                centro: permiso.centro || "-",
                tecnico: [permiso.tecnico_1, permiso.tecnico_2, ...getFirmasTecnicosAdicionales(permiso.firmas_tecnicos_adicionales).map((item) => item.nombre)].filter(Boolean).join(", ") || "-",
                estado: "Permiso creado",
                descripcion: permiso.descripcion_trabajo || "-",
                fechaPrincipal: permiso.fecha_ingreso,
                fechaOrden: parseComparableDate(fechaBase || permiso.fecha_ingreso),
                raw: permiso
            });
        });

        overviewMantenciones.forEach((item) => {
            const cliente = item.empresa || item.cliente || "";
            if (!clienteMatch(cliente)) return;
            const fechaBase = item.created_at || item.updated_at || item.fecha_ingreso;
            pushRow(rows, {
                id: `mant-${item.id_mantencion_terreno}`,
                tipo: "mantencion",
                tipoLabel: "Mantencion",
                correlativo: `N${item.id_mantencion_terreno || "-"}`,
                cliente,
                centro: item.centro || "-",
                tecnico: [item.tecnico_1, item.tecnico_2, ...getFirmasTecnicosAdicionales(item.firmas_tecnicos_adicionales).map((tecnico) => tecnico.nombre)].filter(Boolean).join(", ") || "-",
                estado: "Finalizado",
                descripcion: item.descripcion_trabajo || "-",
                fechaPrincipal: item.fecha_ingreso,
                fechaOrden: parseComparableDate(fechaBase || item.fecha_ingreso),
                raw: item
            });
        });

        overviewRetiros.forEach((item) => {
            const cliente = item.empresa || item.cliente || "";
            if (!clienteMatch(cliente)) return;
            const fechaBase = item.created_at || item.updated_at || item.fecha_retiro;
            const estadoEdicion = String(item.estado_edicion || "finalizado").trim().toLowerCase();
            pushRow(rows, {
                id: `ret-${item.id_retiro_terreno}`,
                tipo: "retiro",
                tipoLabel: "Retiro",
                correlativo: `N${item.id_retiro_terreno || "-"}`,
                cliente,
                centro: item.centro || "-",
                tecnico: [item.tecnico_1, item.tecnico_2, ...getFirmasTecnicosAdicionales(item.firmas_tecnicos_adicionales).map((tecnico) => tecnico.nombre)].filter(Boolean).join(", ") || "-",
                estado:
                    estadoEdicion === "edicion_solicitada"
                        ? "Edicion solicitada"
                        : estadoEdicion === "edicion_autorizada"
                            ? "Edicion autorizada"
                            : estadoEdicion === "edicion_rechazada"
                                ? "Edicion rechazada"
                                : "Finalizado",
                descripcion: item.descripcion_trabajo || "-",
                fechaPrincipal: item.fecha_retiro,
                fechaOrden: parseComparableDate(fechaBase || item.fecha_retiro),
                raw: item
            });
        });

        overviewLevantamientos.forEach((item) => {
            const cliente = item.empresa || item.cliente || "";
            if (!clienteMatch(cliente)) return;
            const fechaBase = item.created_at || item.updated_at || item.fecha_levantamiento;
            pushRow(rows, {
                id: `lev-${item.id_levantamiento_terreno}`,
                tipo: "levantamiento",
                tipoLabel: "Levantamiento",
                correlativo: `N${item.id_levantamiento_terreno || "-"}`,
                cliente,
                centro: item.centro || "-",
                tecnico: "-",
                estado: String(item.estado || "finalizado").trim() || "-",
                descripcion: item.resumen || item.observaciones || "-",
                fechaPrincipal: item.fecha_levantamiento,
                fechaOrden: parseComparableDate(fechaBase || item.fecha_levantamiento),
                raw: item
            });
        });

        return rows.sort((a, b) => {
            const ta = a.fechaOrden ? a.fechaOrden.getTime() : 0;
            const tb = b.fechaOrden ? b.fechaOrden.getTime() : 0;
            return tb - ta;
        });
    }, [
        overviewActasEntrega,
        overviewReapuntamientos,
        overviewPermisos,
        overviewMantenciones,
        overviewRetiros,
        overviewLevantamientos,
        filtroCliente
    ]);

    const overviewRowsPeriodo = useMemo(() => {
        if (overviewPeriodo === "all") return overviewRowsBase;
        const inicioHoy = startOfToday();
        const fechaMinima =
            overviewPeriodo === "month"
                ? startOfCurrentMonth()
                : new Date(inicioHoy.getFullYear(), inicioHoy.getMonth(), inicioHoy.getDate() - (overviewPeriodo === "7d" ? 6 : 29));
        return overviewRowsBase.filter((item) => item.fechaOrden && item.fechaOrden >= fechaMinima);
    }, [overviewRowsBase, overviewPeriodo]);

    const overviewRowsRecientes = useMemo(() => overviewRowsPeriodo.slice(0, 12), [overviewRowsPeriodo]);

    const overviewTotalesRapidos = useMemo(() => {
        const inicioMes = startOfCurrentMonth();
        const inicio30 = new Date(startOfToday().getFullYear(), startOfToday().getMonth(), startOfToday().getDate() - 29);
        return {
            esteMes: overviewRowsBase.filter((item) => item.fechaOrden && item.fechaOrden >= inicioMes).length,
            ultimos30: overviewRowsBase.filter((item) => item.fechaOrden && item.fechaOrden >= inicio30).length
        };
    }, [overviewRowsBase]);

    const overviewCentrosIntervenidos = useMemo(() => {
        const set = new Set(
            overviewRowsPeriodo
                .map((item) => normalizeText(item.centro || ""))
                .filter(Boolean)
        );
        return set.size;
    }, [overviewRowsPeriodo]);

    const overviewResumenCards = useMemo(() => {
        const countBy = (tipo) => overviewRowsPeriodo.filter((item) => item.tipo === tipo).length;
        return [
            { key: "acta_entrega", label: "Instalaciones", value: countBy("acta_entrega"), icon: "fa-file-signature", tone: "blue" },
            { key: "reapuntamiento", label: "Reapuntamientos", value: countBy("reapuntamiento"), icon: "fa-crosshairs", tone: "sky" },
            { key: "intervencion", label: "Permisos", value: countBy("intervencion"), icon: "fa-clipboard-check", tone: "indigo" },
            { key: "mantencion", label: "Mantenciones", value: countBy("mantencion"), icon: "fa-tools", tone: "green" },
            { key: "retiro", label: "Retiros", value: countBy("retiro"), icon: "fa-truck-loading", tone: "amber" },
            { key: "levantamiento", label: "Levantamientos", value: countBy("levantamiento"), icon: "fa-satellite-dish", tone: "red" }
        ];
    }, [overviewRowsPeriodo]);

    const totalActaPages = Math.max(1, Math.ceil(actasFiltradas.length / actaPageSize));
    const totalPermisoPages = Math.max(1, Math.ceil(permisosFiltrados.length / permisoPageSize));
    const actasPaginadas = useMemo(() => {
        const start = (actaPage - 1) * actaPageSize;
        return actasFiltradas.slice(start, start + actaPageSize);
    }, [actasFiltradas, actaPage, actaPageSize]);
    const permisosPaginados = useMemo(() => {
        const start = (permisoPage - 1) * permisoPageSize;
        return permisosFiltrados.slice(start, start + permisoPageSize);
    }, [permisosFiltrados, permisoPage, permisoPageSize]);

    const cambiarTabDetallePermiso = (tab) => setDetallePermisoTab(tab);

    useEffect(() => {
        if (!filtroCentroId) return;
        const exists = centrosFiltroCliente.some((c) => String(c.id || c.id_centro) === String(filtroCentroId));
        if (!exists) setFiltroCentroId("");
    }, [filtroCliente, centrosFiltroCliente, filtroCentroId]);

    useEffect(() => {
        setActaPage(1);
    }, [filtroCliente, filtroCentroId, filtroFechaDesde, filtroFechaHasta, filtroCorrelativo, subcategoria, actaPageSize]);

    useEffect(() => {
        setPermisoPage(1);
    }, [filtroCliente, filtroCentroId, filtroFechaDesde, filtroFechaHasta, filtroCorrelativo, subcategoria, permisoPageSize]);

    useEffect(() => {
        setMostrarDetallePermiso(false);
        setDetallePermisoTab("gps");
        setLevantamientoDetalleId(null);
    }, [subcategoria, filtroCliente, filtroCentroId, filtroFechaDesde, filtroFechaHasta, filtroCorrelativo, permisoPage]);

    useEffect(() => {
        if (actaPage > totalActaPages) setActaPage(totalActaPages);
    }, [actaPage, totalActaPages]);

    useEffect(() => {
        if (permisoPage > totalPermisoPages) setPermisoPage(totalPermisoPages);
    }, [permisoPage, totalPermisoPages]);

    const permisoCentroSeleccionadoForm = useMemo(
        () => centros.find((c) => String(c.id || c.id_centro) === String(permisoCentroIdForm)),
        [centros, permisoCentroIdForm]
    );
    const permisoEquiposDevueltosOrca = useMemo(
        () =>
            (Array.isArray(permisoArmadoEquipos) ? permisoArmadoEquipos : []).filter(
                (item) => normalizeText(item?.estado_uso || "") === "devuelto_bodega"
            ),
        [permisoArmadoEquipos]
    );
    const permisoChecklistMantencion = useMemo(
        () => parseChecklistEquipos(permisoChecklistEquiposRaw),
        [permisoChecklistEquiposRaw]
    );

    const permisosCentrosFormFiltrados = useMemo(() => {
        if (!permisoClienteIdForm) return centrosOrdenados;
        return centrosOrdenados.filter((centro) => getCentroClienteKey(centro) === permisoClienteIdForm);
    }, [centrosOrdenados, permisoClienteIdForm]);

    const resetFormulario = () => {
        setEditandoId(null);
        setClienteIdForm("");
        setCentroIdForm("");
        setFechaRegistro("");
        setRegion("");
        setLocalidad("");
        setTecnico1("");
        setFirmaTecnico1("");
        setTecnico2("");
        setFirmaTecnico2("");
        setTecnicosAdicionalesActa([]);
        setRecepcionaNombre("");
        setFirmaRecepciona("");
        setEquiposConsiderados("");
        setCentroOrigenTraslado("");
        setMostrarEditorActa(false);
    };

    const abrirNuevaActa = () => {
        setEditandoId(null);
        const clienteInicial = filtroCliente !== "all" ? clientesForm.find((c) => c.label === filtroCliente)?.key || "" : "";
        setClienteIdForm(clienteInicial);
        setCentroIdForm(filtroCentroId || "");
        setFechaRegistro(todayLocalInputDate());
        setRegion("");
        setLocalidad("");
        setTecnico1("");
        setFirmaTecnico1("");
        setTecnico2("");
        setFirmaTecnico2("");
        setRecepcionaNombre("");
        setFirmaRecepciona("");
        setEquiposConsiderados("");
        setCentroOrigenTraslado("");
        setMostrarEditorActa(true);
    };

    const resetFormularioPermiso = () => {
        setPermisoEditandoId(null);
        setPermisoActaEntregaId(null);
        setPermisoClienteIdForm("");
        setPermisoCentroIdForm("");
        setPermisoFechaIngreso("");
        setPermisoFechaSalida("");
        setPermisoCorreoCentro("");
        setPermisoTelefonoCentro("");
        setPermisoRegion("");
        setPermisoLocalidad("");
        setPermisoResponsabilidad("");
        setPermisoBaseTierra("");
        setPermisoCantidadRadares("");
        setPermisoTecnico1("");
        setPermisoFirmaTecnico1("");
        setPermisoTecnico2("");
        setPermisoFirmaTecnico2("");
        setPermisoTecnicosAdicionales([]);
        setPermisoRecepcionaNombre("");
        setPermisoRecepcionaRut("");
        setPermisoFirmaRecepciona("");
        setPermisoPuntosGps([{ lat: "", lng: "" }]);
        setPermisoSellos([{ ubicacion: "", numeroAnterior: "", numeroNuevo: "" }]);
        setPermisoArmadoEquipos([]);
        setPermisoMedicionFaseNeutro("");
        setPermisoMedicionNeutroTierra("");
        setPermisoHertz("");
        setPermisoDescripcionTrabajo("");
        setPermisoChecklistEquiposRaw("");
        setPermisoEvidencias([]);
        setPermisoEvidenciaTargetIndex(null);
        setPermisoCambiosEquipo([]);
        setRetiroTipo("parcial");
        setRetiroEquiposChecklist([]);
        setMostrarEditorPermiso(false);
    };
    const abrirNuevoPermiso = () => {
        setPermisoEditandoId(null);
        setPermisoActaEntregaId(null);
        const clienteInicial = filtroCliente !== "all" ? clientesForm.find((c) => c.label === filtroCliente)?.key || "" : "";
        setPermisoClienteIdForm(clienteInicial);
        setPermisoCentroIdForm(filtroCentroId || "");
        setPermisoFechaIngreso(todayLocalInputDate());
        setPermisoFechaSalida("");
        setPermisoCorreoCentro("");
        setPermisoTelefonoCentro("");
        setPermisoRegion("");
        setPermisoLocalidad("");
        setPermisoResponsabilidad("");
        setPermisoBaseTierra("");
        setPermisoCantidadRadares("");
        setPermisoTecnico1("");
        setPermisoFirmaTecnico1("");
        setPermisoTecnico2("");
        setPermisoFirmaTecnico2("");
        setPermisoTecnicosAdicionales([]);
        setPermisoRecepcionaNombre("");
        setPermisoRecepcionaRut("");
        setPermisoFirmaRecepciona("");
        setPermisoPuntosGps([{ lat: "", lng: "" }]);
        setPermisoSellos([{ ubicacion: "", numeroAnterior: "", numeroNuevo: "" }]);
        setPermisoArmadoEquipos([]);
        setPermisoMedicionFaseNeutro("");
        setPermisoMedicionNeutroTierra("");
        setPermisoHertz("");
        setPermisoDescripcionTrabajo("");
        setPermisoChecklistEquiposRaw("");
        setPermisoEvidencias([]);
        setPermisoEvidenciaTargetIndex(null);
        setPermisoCambiosEquipo([]);
        setMostrarEditorPermiso(true);
    };


    useEffect(() => {
        if (!centroIdForm) {
            setRegion("");
            setLocalidad("");
            return;
        }
        const centro = centros.find((c) => String(c.id || c.id_centro) === String(centroIdForm));
        if (!centro) return;
        const regionCentro = centro.area || centro.region || "";
        const localidadCentro = centro.ubicacion || centro.localidad || centro.direccion || "";
        setRegion(regionCentro);
        setLocalidad(localidadCentro);
        const clienteKey = getCentroClienteKey(centro);
        if (clienteKey) setClienteIdForm(clienteKey);
    }, [centroIdForm, centros]);

    useEffect(() => {
        if (!clienteIdForm || !centroIdForm) return;
        const centro = centros.find((c) => String(c.id || c.id_centro) === String(centroIdForm));
        if (!centro) {
            setCentroIdForm("");
            return;
        }
        if (getCentroClienteKey(centro) !== clienteIdForm) {
            setCentroIdForm("");
        }
    }, [clienteIdForm, centroIdForm, centros]);

    const abrirSelectorEvidenciaMantencion = (targetIndex = null) => {
        setPermisoEvidenciaTargetIndex(Number.isInteger(targetIndex) ? targetIndex : null);
        if (permisoEvidenciaInputRef.current) {
            permisoEvidenciaInputRef.current.value = "";
            permisoEvidenciaInputRef.current.click();
        }
    };

    const handleSeleccionarEvidenciaMantencion = (event) => {
        const file = event.target.files?.[0];
        const targetIndex = Number.isInteger(permisoEvidenciaTargetIndex) ? permisoEvidenciaTargetIndex : null;
        event.target.value = "";
        if (!file) return;

        const reader = new FileReader();
        reader.onload = () => {
            const dataUrl = String(reader.result || "").trim();
            if (!dataUrl) return;
            setPermisoEvidencias((prev) => {
                if (targetIndex != null && prev[targetIndex] != null) {
                    const next = [...prev];
                    next[targetIndex] = dataUrl;
                    return next.slice(0, 3);
                }
                if (prev.length >= 3) return prev;
                return [...prev, dataUrl].slice(0, 3);
            });
            setPermisoEvidenciaTargetIndex(null);
        };
        reader.readAsDataURL(file);
    };

    const handleEliminarEvidenciaMantencion = (index) => {
        setPermisoEvidencias((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
        setPermisoEvidenciaTargetIndex(null);
    };

    useEffect(() => {
        if (!permisoCentroIdForm) {
            setPermisoTelefonoCentro("");
            setPermisoRegion("");
            setPermisoLocalidad("");
            setPermisoBaseTierra("");
            setPermisoCantidadRadares("");
            return;
        }
        const centro = centros.find((c) => String(c.id || c.id_centro) === String(permisoCentroIdForm));
        if (!centro) return;
        setPermisoTelefonoCentro(centro.telefono || "");
        setPermisoRegion(centro.area || centro.region || "");
        setPermisoLocalidad(centro.ubicacion || centro.localidad || centro.direccion || "");
        setPermisoBaseTierra(normalizeBoolSelectValue(centro.base_tierra));
        setPermisoCantidadRadares(centro.cantidad_radares == null ? "" : String(centro.cantidad_radares));
        const clienteKey = getCentroClienteKey(centro);
        if (clienteKey) setPermisoClienteIdForm(clienteKey);
    }, [permisoCentroIdForm, centros]);


    useEffect(() => {
        if (!permisoClienteIdForm || !permisoCentroIdForm) return;
        const centro = centros.find((c) => String(c.id || c.id_centro) === String(permisoCentroIdForm));
        if (!centro) {
            setPermisoCentroIdForm("");
            return;
        }
        if (getCentroClienteKey(centro) !== permisoClienteIdForm) {
            setPermisoCentroIdForm("");
        }
    }, [permisoClienteIdForm, permisoCentroIdForm, centros]);

    const cargarActas = async () => {
        if (!["acta_entrega", "reapuntamiento"].includes(subcategoria)) return;
        setLoadingActas(true);
        try {
            const data = await obtenerActasEntrega({
                centro_id: filtroCentroId || undefined,
                fecha_desde: filtroFechaDesde || undefined,
                fecha_hasta: filtroFechaHasta || undefined,
                tipo_instalacion:
                    subcategoria === "reapuntamiento" ? "reapuntamiento" : "instalacion"
            });
            setActasEntrega(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error("Error al cargar actas de entrega:", error);
            setActasEntrega([]);
        } finally {
            setLoadingActas(false);
        }
    };

    const cargarPermisos = async (opts = {}) => {
        const silent = Boolean(opts?.silent);
        if (!["intervencion", "mantencion", "retiro", "levantamiento"].includes(subcategoria)) return;
        if (!silent) setLoadingActas(true);
        try {
            const fetcher =
                subcategoria === "mantencion"
                    ? obtenerMantencionesTerreno
                    : subcategoria === "retiro"
                        ? obtenerRetirosTerreno
                        : subcategoria === "levantamiento"
                            ? obtenerLevantamientosTerreno
                        : obtenerPermisosTrabajo;
            const data = await fetcher({
                centro_id: filtroCentroId || undefined,
                fecha_desde: filtroFechaDesde || undefined,
                fecha_hasta: filtroFechaHasta || undefined
            });
            setPermisosTrabajo(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error("Error al cargar registros:", error);
            if (!silent) setPermisosTrabajo([]);
        } finally {
            if (!silent) setLoadingActas(false);
        }
    };

    useEffect(() => {
        const fetchCentros = async () => {
            setLoadingCentros(true);
            try {
                const data = await cargarCentrosClientes();
                setCentros(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error("Error al cargar centros para informes:", error);
                setCentros([]);
            } finally {
                setLoadingCentros(false);
            }
        };
        fetchCentros();
    }, []);

    useEffect(() => {
        let activo = true;
        const cargarOverview = async () => {
            setOverviewLoading(true);
            try {
                const commonParams = {
                    centro_id: filtroCentroId || undefined,
                    fecha_desde: filtroFechaDesde || undefined,
                    fecha_hasta: filtroFechaHasta || undefined
                };
                const [
                    actasInstalacion,
                    actasReapuntamiento,
                    permisosIntervencion,
                    mantenciones,
                    retiros,
                    levantamientos
                ] = await Promise.all([
                    obtenerActasEntrega({ ...commonParams, tipo_instalacion: "instalacion" }),
                    obtenerActasEntrega({ ...commonParams, tipo_instalacion: "reapuntamiento" }),
                    obtenerPermisosTrabajo(commonParams),
                    obtenerMantencionesTerreno(commonParams),
                    obtenerRetirosTerreno(commonParams),
                    obtenerLevantamientosTerreno(commonParams)
                ]);
                if (!activo) return;
                setOverviewActasEntrega(Array.isArray(actasInstalacion) ? actasInstalacion : []);
                setOverviewReapuntamientos(Array.isArray(actasReapuntamiento) ? actasReapuntamiento : []);
                setOverviewPermisos(Array.isArray(permisosIntervencion) ? permisosIntervencion : []);
                setOverviewMantenciones(Array.isArray(mantenciones) ? mantenciones : []);
                setOverviewRetiros(Array.isArray(retiros) ? retiros : []);
                setOverviewLevantamientos(Array.isArray(levantamientos) ? levantamientos : []);
            } catch (error) {
                if (!activo) return;
                console.error("Error al cargar overview de informes:", error);
                setOverviewActasEntrega([]);
                setOverviewReapuntamientos([]);
                setOverviewPermisos([]);
                setOverviewMantenciones([]);
                setOverviewRetiros([]);
                setOverviewLevantamientos([]);
            } finally {
                if (activo) setOverviewLoading(false);
            }
        };
        cargarOverview();
        return () => {
            activo = false;
        };
    }, [filtroCentroId, filtroFechaDesde, filtroFechaHasta]);

    useEffect(() => {
        if (subcategoria === "acta_entrega" || subcategoria === "reapuntamiento") {
            cargarActas();
        } else if (subcategoria === "intervencion" || subcategoria === "mantencion" || subcategoria === "retiro" || subcategoria === "levantamiento") {
            cargarPermisos();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subcategoria, filtroCentroId, filtroFechaDesde, filtroFechaHasta]);

    useEffect(() => {
        if (subcategoria !== "levantamiento") return undefined;
        const timer = setInterval(() => {
            cargarPermisos({ silent: true });
        }, 10000);
        return () => clearInterval(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subcategoria, filtroCentroId, filtroFechaDesde, filtroFechaHasta]);

    const handleGuardarActa = async () => {
        if (!centroIdForm || !fechaRegistro) {
            alert("Centro y fecha de registro son obligatorios.");
            return;
        }
        const firmasTecnicosAdicionales = tecnicosAdicionalesActa
            .map((item) => ({
                nombre: String(item?.nombre || "").trim(),
                firma: item?.firma || ""
            }))
            .filter((item) => item.nombre);
        const payload = {
            centro_id: Number(centroIdForm),
            fecha_registro: fechaRegistro,
            region,
            localidad,
            tecnico_1: tecnico1,
            firma_tecnico_1: firmaTecnico1,
            tecnico_2: tecnico2,
            firma_tecnico_2: firmaTecnico2,
            firmas_tecnicos_adicionales: firmasTecnicosAdicionales,
            recepciona_nombre: recepcionaNombre,
            firma_recepciona: firmaRecepciona,
            equipos_considerados: equiposConsiderados,
            centro_origen_traslado: centroOrigenTraslado,
            tipo_instalacion: subcategoria === "reapuntamiento" ? "reapuntamiento" : "instalacion"
        };

        setSavingActa(true);
        try {
            if (editandoId) {
                await actualizarActaEntrega(editandoId, payload);
            } else {
                await crearActaEntrega(payload);
            }
            await cargarActas();
            resetFormulario();
        } catch (error) {
            console.error("Error al guardar acta:", error);
            alert("No se pudo guardar el acta de entrega.");
        } finally {
            setSavingActa(false);
        }
    };

    const handleEditarActa = (acta) => {
        setEditandoId(acta.id_acta_entrega);
        setCentroIdForm(String(acta.centro_id || ""));
        setFechaRegistro(toInputDate(acta.fecha_registro));
        setRegion(acta.region || "");
        setLocalidad(acta.localidad || "");
        setTecnico1(acta.tecnico_1 || "");
        setFirmaTecnico1(acta.firma_tecnico_1 || "");
        setTecnico2(acta.tecnico_2 || "");
        setFirmaTecnico2(acta.firma_tecnico_2 || "");
        setTecnicosAdicionalesActa(getTecnicosAdicionalesActa(acta));
        setRecepcionaNombre(acta.recepciona_nombre || "");
        setFirmaRecepciona(acta.firma_recepciona || "");
        setEquiposConsiderados(acta.equipos_considerados || "");
        setCentroOrigenTraslado(acta.centro_origen_traslado || "");
        const centro = centros.find((c) => String(c.id || c.id_centro) === String(acta.centro_id || ""));
        if (centro) {
            setClienteIdForm(getCentroClienteKey(centro));
        }
        setMostrarEditorActa(true);
    };

    const agregarTecnicoAdicionalActa = () => {
        setTecnicosAdicionalesActa((prev) => [...prev, { nombre: "", firma: "" }]);
    };

    const actualizarTecnicoAdicionalActa = (index, campo, valor) => {
        setTecnicosAdicionalesActa((prev) =>
            prev.map((item, idx) => (idx === index ? { ...item, [campo]: valor } : item))
        );
    };

    const eliminarTecnicoAdicionalActa = (index) => {
        setTecnicosAdicionalesActa((prev) => prev.filter((_, idx) => idx !== index));
    };

    const agregarTecnicoAdicionalPermiso = () => {
        setPermisoTecnicosAdicionales((prev) => [...prev, { nombre: "", firma: "" }]);
    };

    const actualizarTecnicoAdicionalPermiso = (index, campo, valor) => {
        setPermisoTecnicosAdicionales((prev) =>
            prev.map((item, idx) => (idx === index ? { ...item, [campo]: valor } : item))
        );
    };

    const eliminarTecnicoAdicionalPermiso = (index) => {
        setPermisoTecnicosAdicionales((prev) => prev.filter((_, idx) => idx !== index));
    };

    const handleEliminarActa = async (id) => {
        if (!window.confirm("Quieres eliminar esta acta de entrega?")) return;
        try {
            await eliminarActaEntrega(id);
            await cargarActas();
            if (editandoId === id) resetFormulario();
        } catch (error) {
            console.error("Error al eliminar acta:", error);
            alert("No se pudo eliminar el acta.");
        }
    };

    const handleAbrirPermisoDesdeActa = async (acta) => {
        const centroId = Number(acta?.centro_id || 0);
        if (!centroId) {
            alert("Acta sin centro asociado.");
            return;
        }
        try {
            let permiso = permisosTrabajo.find((p) => Number(p.centro_id || 0) === centroId);
            if (!permiso) {
                const data = await obtenerPermisosTrabajo({ centro_id: centroId });
                const lista = Array.isArray(data) ? data : [];
                if (lista.length) {
                    permiso = lista[0];
                    setPermisosTrabajo((prev) => {
                        const ids = new Set(prev.map((x) => Number(x.id_permiso_trabajo || 0)));
                        const extra = lista.filter((x) => !ids.has(Number(x.id_permiso_trabajo || 0)));
                        return extra.length ? [...prev, ...extra] : prev;
                    });
                }
            }

            if (permiso) {
                handleEditarPermiso(permiso);
                return;
            }

            const centro = centros.find((c) => Number(c.id || c.id_centro || 0) === centroId);
            setPermisoEditandoId(null);
            setPermisoActaEntregaId(acta.id_acta_entrega || null);
            setPermisoCentroIdForm(String(centroId));
            setPermisoFechaIngreso(todayLocalInputDate());
            setPermisoFechaSalida("");
            setPermisoCorreoCentro(centro?.correo_centro || "");
            setPermisoTelefonoCentro(centro?.telefono || "");
            setPermisoRegion(centro?.area || centro?.region || acta.region || "");
            setPermisoLocalidad(centro?.ubicacion || centro?.localidad || centro?.direccion || acta.localidad || "");
            setPermisoBaseTierra(normalizeBoolSelectValue(centro?.base_tierra));
            setPermisoCantidadRadares(centro?.cantidad_radares == null ? "" : String(centro.cantidad_radares));
            setPermisoTecnico1(acta.tecnico_1 || "");
            setPermisoFirmaTecnico1(acta.firma_tecnico_1 || "");
            setPermisoTecnico2(acta.tecnico_2 || "");
            setPermisoFirmaTecnico2(acta.firma_tecnico_2 || "");
            setPermisoTecnicosAdicionales(getFirmasTecnicosAdicionales(acta.firmas_tecnicos_adicionales).map((item) => ({
                nombre: String(item?.nombre || "").trim(),
                firma: item?.firma || ""
            })));
            setPermisoRecepcionaNombre(acta.recepciona_nombre || "");
            setPermisoRecepcionaRut("");
            setPermisoFirmaRecepciona(acta.firma_recepciona || "");
            setPermisoPuntosGps([{ lat: "", lng: "" }]);
            setPermisoSellos([{ ubicacion: "", numeroAnterior: "", numeroNuevo: "" }]);
            setPermisoArmadoEquipos(Array.isArray(acta.armado_equipos) ? acta.armado_equipos : []);
            setPermisoMedicionFaseNeutro("");
            setPermisoMedicionNeutroTierra("");
            setPermisoHertz("");
            setPermisoDescripcionTrabajo("");
            if (centro) setPermisoClienteIdForm(getCentroClienteKey(centro));
            setMostrarEditorPermiso(true);
        } catch (error) {
            console.error("Error al abrir permiso desde acta:", error);
            alert("No se pudo abrir el permiso de trabajo.");
        }
    };

    const handleGuardarPermiso = async () => {
        if (!permisoCentroIdForm || !permisoFechaIngreso) {
            alert("Centro y fecha de ingreso son obligatorios.");
            return;
        }
        const gpsRows = permisoPuntosGps.map((p) => ({
            lat: String(p?.lat || "").trim(),
            lng: String(p?.lng || "").trim()
        }));
        const hasPartialGps = gpsRows.some((p) => (p.lat && !p.lng) || (!p.lat && p.lng));
        if (hasPartialGps) {
            alert("Completa latitud y longitud en cada punto GPS.");
            return;
        }
        const gpsSerialized = gpsRows
            .filter((p) => p.lat && p.lng)
            .map((p) => `${p.lat},${p.lng}`)
            .join(" | ");
        const selloRows = permisoSellos.map((item) => ({
            ubicacion: String(item?.ubicacion || "").trim(),
            numero_anterior: String(item?.numeroAnterior || "").trim(),
            numero_nuevo: String(item?.numeroNuevo || "").trim()
        }));
        const hasInvalidSello = selloRows.some(
            (item) =>
                (!item.ubicacion && (item.numero_anterior || item.numero_nuevo)) ||
                (item.ubicacion && !item.numero_anterior && !item.numero_nuevo)
        );
        if (hasInvalidSello) {
            alert("Completa ubicacion y al menos un numero de sello antiguo o nuevo.");
            return;
        }
        const sellosSerialized = JSON.stringify(
            selloRows.filter((item) => item.ubicacion && (item.numero_anterior || item.numero_nuevo))
        );
        const firmasTecnicosAdicionales = permisoTecnicosAdicionales
            .map((item) => ({
                nombre: String(item?.nombre || "").trim(),
                firma: item?.firma || ""
            }))
            .filter((item) => item.nombre);

        const payload = {
            centro_id: Number(permisoCentroIdForm),
            acta_entrega_id: permisoActaEntregaId || null,
            fecha_ingreso: permisoFechaIngreso,
            fecha_salida: permisoFechaSalida || null,
            correo_centro: permisoCorreoCentro || null,
            telefono_centro: permisoTelefonoCentro || null,
            region: permisoRegion || null,
            localidad: permisoLocalidad || null,
            responsabilidad: permisoResponsabilidad || null,
            base_tierra: permisoBaseTierra || null,
            cantidad_radares: permisoCantidadRadares || null,
            tecnico_1: permisoTecnico1 || null,
            firma_tecnico_1: permisoFirmaTecnico1 || null,
            tecnico_2: permisoTecnico2 || null,
            firma_tecnico_2: permisoFirmaTecnico2 || null,
            firmas_tecnicos_adicionales: firmasTecnicosAdicionales,
            recepciona_nombre: permisoRecepcionaNombre || null,
            recepciona_rut: permisoRecepcionaRut || null,
            firma_recepciona: permisoFirmaRecepciona || null,
            puntos_gps: gpsSerialized || null,
            sellos: sellosSerialized === "[]" ? null : sellosSerialized,
            medicion_fase_neutro: normalizeMeasureInput(permisoMedicionFaseNeutro) || null,
            medicion_neutro_tierra: normalizeMeasureInput(permisoMedicionNeutroTierra) || null,
            hertz: normalizeMeasureInput(permisoHertz) || null,
            descripcion_trabajo: permisoDescripcionTrabajo || null
        };
        if (subcategoria === "mantencion") {
            payload.evidencia_foto = permisoEvidencias.length ? JSON.stringify(permisoEvidencias.slice(0, 3)) : null;
        }

        setSavingPermiso(true);
        try {
            if (subcategoria === "mantencion") {
                if (permisoEditandoId) {
                    await actualizarMantencionTerreno(permisoEditandoId, payload);
                } else {
                    await crearMantencionTerreno(payload);
                }
            } else {
                if (permisoEditandoId) {
                    await actualizarPermisoTrabajo(permisoEditandoId, payload);
                } else {
                    await crearPermisoTrabajo(payload);
                }
            }
            await cargarPermisos();
            resetFormularioPermiso();
        } catch (error) {
            console.error(`Error al guardar ${subcategoria === "mantencion" ? "mantencion" : "permiso de trabajo"}:`, error);
            alert(subcategoria === "mantencion" ? "No se pudo guardar la mantencion en terreno." : "No se pudo guardar el permiso de trabajo.");
        } finally {
            setSavingPermiso(false);
        }
    };

    const handleGuardarRetiro = async () => {
        if (!permisoEditandoId) {
            alert("No hay un retiro seleccionado para editar.");
            return;
        }
        if (!permisoCentroIdForm || !permisoFechaIngreso) {
            alert("Debes indicar centro y fecha del retiro.");
            return;
        }

        const equiposPayload = (Array.isArray(retiroEquiposChecklist) ? retiroEquiposChecklist : []).map((item) => {
            const retirado = !!item?.retirado;
            const recibidoBodega = !!item?.recibido_bodega;
            const estadoLogistico = String(item?.estado_logistico || "").trim().toLowerCase();
            return {
                id_retiro_equipo: item?.id_retiro_equipo || null,
                equipo_id: item?.equipo_id || null,
                equipo_nombre: item?.equipo_nombre || null,
                numero_serie: item?.numero_serie || null,
                codigo: item?.codigo || null,
                retirado,
                modalidad_retorno: normalizeRetiroModalidad(item?.modalidad_retorno),
                recibido_bodega: recibidoBodega,
                estado_logistico: estadoLogistico || (recibidoBodega ? "recepcionado_bodega" : (retirado ? "en_transito_bodega" : "sin_movimiento"))
            };
        });

        const payload = {
            centro_id: Number(permisoCentroIdForm),
            fecha_retiro: permisoFechaIngreso,
            tipo_retiro: retiroTipo === "completo" ? "completo" : "parcial",
            estado_logistico: getRetiroEstadoPayload(equiposPayload),
            observacion: permisoDescripcionTrabajo || null,
            tecnico_1: permisoTecnico1 || null,
            firma_tecnico_1: permisoFirmaTecnico1 || null,
            tecnico_2: permisoTecnico2 || null,
            firma_tecnico_2: permisoFirmaTecnico2 || null,
            recepciona_nombre: permisoRecepcionaNombre || null,
            recepciona_rut: permisoRecepcionaRut || null,
            firma_recepciona: permisoFirmaRecepciona || null,
            equipos: equiposPayload
        };

        setSavingPermiso(true);
        try {
            await updateRetiroTerreno(permisoEditandoId, payload);
            await cargarPermisos();
            resetFormularioPermiso();
        } catch (error) {
            console.error("Error al guardar retiro en terreno:", error);
            alert("No se pudo guardar el retiro en terreno.");
        } finally {
            setSavingPermiso(false);
        }
    };

    const handleEditarPermiso = (permiso) => {
        setPermisoEditandoId(permiso.id_permiso_trabajo);
        setPermisoActaEntregaId(permiso.acta_entrega_id || null);
        setPermisoCentroIdForm(String(permiso.centro_id || ""));
        setPermisoFechaIngreso(toInputDate(permiso.fecha_ingreso));
        setPermisoFechaSalida(toInputDate(permiso.fecha_salida));
        setPermisoCorreoCentro(permiso.correo_centro || "");
        setPermisoTelefonoCentro(permiso.telefono_centro || "");
        setPermisoRegion(permiso.region || "");
        setPermisoLocalidad(permiso.localidad || "");
        setPermisoResponsabilidad(permiso.responsabilidad || "");
        setPermisoBaseTierra(normalizeBoolSelectValue(permiso.base_tierra));
        setPermisoCantidadRadares(permiso.cantidad_radares == null ? "" : String(permiso.cantidad_radares));
        setPermisoTecnico1(permiso.tecnico_1 || "");
        setPermisoFirmaTecnico1(permiso.firma_tecnico_1 || "");
        setPermisoTecnico2(permiso.tecnico_2 || "");
        setPermisoFirmaTecnico2(permiso.firma_tecnico_2 || "");
        setPermisoTecnicosAdicionales(getFirmasTecnicosAdicionales(permiso.firmas_tecnicos_adicionales).map((item) => ({
            nombre: String(item?.nombre || "").trim(),
            firma: item?.firma || ""
        })));
        setPermisoRecepcionaNombre(permiso.recepciona_nombre || "");
        setPermisoRecepcionaRut(permiso.recepciona_rut || "");
        setPermisoFirmaRecepciona(permiso.firma_recepciona || "");
        setPermisoPuntosGps(parseGpsPoints(permiso.puntos_gps));
        setPermisoSellos(parseSellos(permiso.sellos));
        setPermisoArmadoEquipos(Array.isArray(permiso.armado_equipos) ? permiso.armado_equipos : []);
        setPermisoMedicionFaseNeutro(normalizeMeasureInput(permiso.medicion_fase_neutro || ""));
        setPermisoMedicionNeutroTierra(normalizeMeasureInput(permiso.medicion_neutro_tierra || ""));
        setPermisoHertz(normalizeMeasureInput(permiso.hertz || ""));
        setPermisoDescripcionTrabajo(permiso.descripcion_trabajo || "");
        setPermisoChecklistEquiposRaw("");
        setPermisoEvidencias([]);
        setPermisoEvidenciaTargetIndex(null);
        setPermisoCambiosEquipo([]);
        const centro = centros.find((c) => String(c.id || c.id_centro) === String(permiso.centro_id || ""));
        if (centro) setPermisoClienteIdForm(getCentroClienteKey(centro));
        setMostrarEditorPermiso(true);
    };

    const handleEditarMantencion = async (mantencion) => {
        setPermisoEditandoId(mantencion.id_mantencion_terreno);
        setPermisoActaEntregaId(null);
        setPermisoCentroIdForm(String(mantencion.centro_id || ""));
        setPermisoFechaIngreso(toInputDate(mantencion.fecha_ingreso));
        setPermisoFechaSalida(toInputDate(mantencion.fecha_salida));
        setPermisoCorreoCentro(mantencion.correo_centro || "");
        setPermisoTelefonoCentro(mantencion.telefono_centro || "");
        setPermisoRegion(mantencion.region || "");
        setPermisoLocalidad(mantencion.localidad || "");
        setPermisoResponsabilidad(mantencion.responsabilidad || "");
        setPermisoBaseTierra(normalizeBoolSelectValue(mantencion.base_tierra));
        setPermisoCantidadRadares(mantencion.cantidad_radares == null ? "" : String(mantencion.cantidad_radares));
        setPermisoTecnico1(mantencion.tecnico_1 || "");
        setPermisoFirmaTecnico1(mantencion.firma_tecnico_1 || "");
        setPermisoTecnico2(mantencion.tecnico_2 || "");
        setPermisoFirmaTecnico2(mantencion.firma_tecnico_2 || "");
        setPermisoTecnicosAdicionales(getFirmasTecnicosAdicionales(mantencion.firmas_tecnicos_adicionales).map((item) => ({
            nombre: String(item?.nombre || "").trim(),
            firma: item?.firma || ""
        })));
        setPermisoRecepcionaNombre(mantencion.recepciona_nombre || "");
        setPermisoRecepcionaRut(mantencion.recepciona_rut || "");
        setPermisoFirmaRecepciona(mantencion.firma_recepciona || "");
        setPermisoPuntosGps(parseGpsPoints(mantencion.puntos_gps));
        setPermisoSellos(parseSellos(mantencion.sellos));
        setPermisoArmadoEquipos([]);
        setPermisoMedicionFaseNeutro(normalizeMeasureInput(mantencion.medicion_fase_neutro || ""));
        setPermisoMedicionNeutroTierra(normalizeMeasureInput(mantencion.medicion_neutro_tierra || ""));
        setPermisoHertz(normalizeMeasureInput(mantencion.hertz || ""));
        setPermisoDescripcionTrabajo(mantencion.descripcion_trabajo || "");
        setPermisoChecklistEquiposRaw(mantencion.checklist_equipos || "");
        setPermisoEvidencias(parseEvidencePhotos(mantencion.evidencia_foto || ""));
        setPermisoEvidenciaTargetIndex(null);
        setPermisoCambiosEquipo(Array.isArray(mantencion.cambios_equipo) ? mantencion.cambios_equipo : []);
        const centro = centros.find((c) => String(c.id || c.id_centro) === String(mantencion.centro_id || ""));
        if (centro) setPermisoClienteIdForm(getCentroClienteKey(centro));
        setMostrarEditorPermiso(true);
        try {
            const cambios = await obtenerCambiosEquipoMantencion(mantencion.id_mantencion_terreno);
            setPermisoCambiosEquipo(Array.isArray(cambios) ? cambios : []);
        } catch (error) {
            console.error("Error al cargar cambios de equipo para edicion:", error);
        }
    };

    const handleEditarRetiro = async (retiro) => {
        const retiroId = retiro?.id_retiro_terreno;
        if (!retiroId) return;

        const estadoEdicion = normalizeText(retiro?.estado_edicion || "finalizado");
        try {
            if (estadoEdicion === "finalizado" || estadoEdicion === "edicion_rechazada") {
                await solicitarEdicionRetiroTerreno(retiroId);
                await resolverEdicionRetiroTerreno(retiroId, { aprobar: true });
            } else if (estadoEdicion === "edicion_solicitada") {
                await resolverEdicionRetiroTerreno(retiroId, { aprobar: true });
            }
        } catch (error) {
            console.error("Error al habilitar edicion del retiro:", error);
            alert("No se pudo habilitar la edicion del retiro.");
            return;
        }

        const centro = centros.find((c) => String(c.id || c.id_centro) === String(retiro.centro_id || ""));
        setPermisoEditandoId(retiroId);
        setPermisoActaEntregaId(null);
        setPermisoCentroIdForm(String(retiro.centro_id || ""));
        setPermisoFechaIngreso(toInputDate(retiro.fecha_retiro));
        setPermisoFechaSalida("");
        setPermisoCorreoCentro(retiro.correo_centro || centro?.correo_centro || centro?.correo || "");
        setPermisoTelefonoCentro(retiro.telefono_centro || centro?.telefono || "");
        setPermisoRegion(retiro.region || centro?.area || centro?.region || "");
        setPermisoLocalidad(retiro.localidad || centro?.ubicacion || centro?.localidad || centro?.direccion || "");
        setPermisoResponsabilidad("");
        setPermisoBaseTierra(normalizeBoolSelectValue(centro?.base_tierra));
        setPermisoCantidadRadares(centro?.cantidad_radares == null ? "" : String(centro.cantidad_radares));
        setPermisoTecnico1(retiro.tecnico_1 || "");
        setPermisoFirmaTecnico1(retiro.firma_tecnico_1 || "");
        setPermisoTecnico2(retiro.tecnico_2 || "");
        setPermisoFirmaTecnico2(retiro.firma_tecnico_2 || "");
        setPermisoTecnicosAdicionales([]);
        setPermisoRecepcionaNombre(retiro.recepciona_nombre || "");
        setPermisoRecepcionaRut(retiro.recepciona_rut || "");
        setPermisoFirmaRecepciona(retiro.firma_recepciona || "");
        setPermisoPuntosGps([{ lat: "", lng: "" }]);
        setPermisoSellos([{ ubicacion: "", numeroAnterior: "", numeroNuevo: "" }]);
        setPermisoArmadoEquipos([]);
        setPermisoMedicionFaseNeutro("");
        setPermisoMedicionNeutroTierra("");
        setPermisoHertz("");
        setPermisoDescripcionTrabajo(retiro.observacion || "");
        setPermisoChecklistEquiposRaw("");
        setPermisoEvidencias([]);
        setPermisoEvidenciaTargetIndex(null);
        setPermisoCambiosEquipo([]);
        setRetiroTipo(String(retiro.tipo_retiro || "").trim().toLowerCase() === "completo" ? "completo" : "parcial");
        setRetiroEquiposChecklist(
            (Array.isArray(retiro.equipos) ? retiro.equipos : []).map((item) => ({
                id_retiro_equipo: item?.id_retiro_equipo || null,
                equipo_id: item?.equipo_id || null,
                equipo_nombre: item?.equipo_nombre || "",
                numero_serie: item?.numero_serie || "",
                codigo: item?.codigo || "",
                retirado: !!item?.retirado,
                modalidad_retorno: normalizeRetiroModalidad(item?.modalidad_retorno),
                recibido_bodega: !!item?.recibido_bodega,
                estado_logistico: item?.estado_logistico || (item?.recibido_bodega ? "recepcionado_bodega" : (item?.retirado ? "en_transito_bodega" : "sin_movimiento"))
            }))
        );
        if (centro) setPermisoClienteIdForm(getCentroClienteKey(centro));
        setMostrarEditorPermiso(true);
        await cargarPermisos({ silent: true });
    };

    const handleEliminarPermiso = async (id) => {
        if (!window.confirm("Quieres eliminar este permiso de trabajo?")) return;
        try {
            await eliminarPermisoTrabajo(id);
            await cargarPermisos();
            if (permisoEditandoId === id) resetFormularioPermiso();
        } catch (error) {
            console.error("Error al eliminar permiso de trabajo:", error);
            alert("No se pudo eliminar el permiso de trabajo.");
        }
    };

    const handleEliminarMantencion = async (id) => {
        if (!window.confirm("Quieres eliminar esta mantencion en terreno?")) return;
        try {
            await eliminarMantencionTerreno(id);
            await cargarPermisos();
            if (permisoEditandoId === id) resetFormularioPermiso();
        } catch (error) {
            console.error("Error al eliminar mantencion en terreno:", error);
            alert("No se pudo eliminar la mantencion en terreno.");
        }
    };

    const handleEliminarRetiro = async (id) => {
        if (!window.confirm("Quieres eliminar este retiro en terreno?")) return;
        try {
            await eliminarRetiroTerreno(id);
            await cargarPermisos();
        } catch (error) {
            console.error("Error al eliminar retiro en terreno:", error);
            alert("No se pudo eliminar el retiro en terreno.");
        }
    };

    const handleEliminarLevantamiento = async (id) => {
        if (!window.confirm("Quieres eliminar este levantamiento en terreno?")) return;
        try {
            await eliminarLevantamientoTerreno(id);
            await cargarPermisos();
            if (String(levantamientoDetalleId || "") === String(id || "")) {
                setLevantamientoDetalleId(null);
            }
        } catch (error) {
            console.error("Error al eliminar levantamiento en terreno:", error);
            alert("No se pudo eliminar el levantamiento en terreno.");
        }
    };

    const handleResolverEdicionLevantamiento = async (id, aprobar) => {
        const txt = aprobar ? "aprobar" : "rechazar";
        if (!window.confirm(`Vas a ${txt} esta solicitud de edicion. Continuar?`)) return;
        try {
            await resolverEdicionLevantamientoTerreno(id, { aprobar });
            await cargarPermisos();
        } catch (error) {
            console.error("Error al resolver solicitud de edicion:", error);
            alert("No se pudo resolver la solicitud.");
        }
    };

    const handleResolverEdicionRetiro = async (id, aprobar) => {
        const txt = aprobar ? "aprobar" : "rechazar";
        if (!window.confirm(`Vas a ${txt} esta solicitud de edicion. Continuar?`)) return;
        try {
            await resolverEdicionRetiroTerreno(id, { aprobar });
            await cargarPermisos();
        } catch (error) {
            console.error("Error al resolver solicitud de edicion del retiro:", error);
            alert("No se pudo resolver la solicitud.");
        }
    };

    const renderPermisoEditor = () => (
        <div className="informes-editor-overlay">
            <div className="card informes-editor-card">
                <div className="card-header d-flex justify-content-between align-items-center informes-editor-header">
                    <div>
                        <h5 className="mb-0">
                            {subcategoria === "mantencion"
                                ? (permisoEditandoId ? "Editar mantencion en terreno" : "Nueva mantencion en terreno")
                                : (permisoEditandoId ? "Editar permiso de trabajo" : "Nuevo permiso de trabajo")}
                        </h5>
                        <small className="informes-editor-subtitle">
                            {subcategoria === "mantencion"
                                ? "Actualiza los datos del informe de mantencion, recepcion, GPS, mediciones y firmas."
                                : "Completa los datos del centro, equipo tecnico, recepcion, GPS, mediciones y firmas."}
                        </small>
                    </div>
                    <button className="btn btn-sm btn-outline-secondary informes-editor-close-btn" onClick={resetFormularioPermiso}>
                        <i className="fas fa-times" />
                    </button>
                </div>
                <div className="card-body">
                    <div className="informes-editor-section">
                        <h6 className="informes-editor-section-title">Datos del centro</h6>
                        <div className="informes-editor-subsection">
                            <div className="informes-editor-subsection-head">
                                <div>
                                    <strong>Informacion del centro</strong>
                                    <small>Datos de referencia cargados automaticamente.</small>
                                </div>
                            </div>
                            <div className="informes-info-grid">
                                <div className="informes-info-card">
                                    <small>Empresa</small>
                                    <strong>{permisoCentroSeleccionadoForm?.cliente || "-"}</strong>
                                </div>
                                <div className="informes-info-card">
                                    <small>Centro</small>
                                    <strong>{permisoCentroSeleccionadoForm?.nombre || "-"}</strong>
                                </div>
                                <div className="informes-info-card">
                                    <small>Codigo ponton</small>
                                    <strong>{permisoCentroSeleccionadoForm?.nombre_ponton || "-"}</strong>
                                </div>
                                <div className="informes-info-card">
                                    <small>Region</small>
                                    <strong>{permisoRegion || "-"}</strong>
                                </div>
                                <div className="informes-info-card">
                                    <small>Localidad</small>
                                    <strong>{permisoLocalidad || "-"}</strong>
                                </div>
                                <div className="informes-info-card">
                                    <small>Telefono centro</small>
                                    <strong>{permisoTelefonoCentro || "-"}</strong>
                                </div>
                                <div className="informes-info-card">
                                    <small>Base tierra</small>
                                    <strong>{permisoBaseTierra === "si" ? "Si" : permisoBaseTierra === "no" ? "No" : "-"}</strong>
                                </div>
                                <div className="informes-info-card">
                                    <small>Cantidad radares</small>
                                    <strong>{permisoCantidadRadares || "-"}</strong>
                                </div>
                                {subcategoria === "mantencion" ? (
                                    <div className="informes-info-card">
                                        <small>Responsabilidad</small>
                                        <strong>{permisoResponsabilidad || "-"}</strong>
                                    </div>
                                ) : null}
                            </div>
                        </div>
                        <div className="informes-editor-subsection">
                            <div className="informes-editor-subsection-head">
                                <div>
                                    <strong>Seleccion y registro</strong>
                                    <small>Campos editables del permiso.</small>
                                </div>
                            </div>
                            <div className="informes-filtros-grid">
                                <div>
                                    <label>Cliente</label>
                                    <select className="form-control" value={permisoClienteIdForm} onChange={(e) => setPermisoClienteIdForm(e.target.value)}>
                                        <option value="">Seleccionar cliente</option>
                                        {clientesForm.map((cliente) => (
                                            <option key={cliente.key} value={cliente.key}>
                                                {cliente.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label>Centro</label>
                                    <select className="form-control" value={permisoCentroIdForm} onChange={(e) => setPermisoCentroIdForm(e.target.value)}>
                                        <option value="">Seleccionar centro</option>
                                        {permisosCentrosFormFiltrados.map((centro) => (
                                            <option key={centro.id || centro.id_centro} value={centro.id || centro.id_centro}>
                                                {centro.nombre}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                {subcategoria === "mantencion" ? (
                                    <div>
                                        <label>Responsabilidad</label>
                                        <select className="form-control" value={permisoResponsabilidad} onChange={(e) => setPermisoResponsabilidad(e.target.value)}>
                                            <option value="">Seleccionar responsabilidad</option>
                                            <option value="orca">Orca</option>
                                            <option value="cliente">Cliente</option>
                                        </select>
                                    </div>
                                ) : null}
                                <div>
                                    <label>Fecha ingreso</label>
                                    <input className="form-control" type="date" value={permisoFechaIngreso} onChange={(e) => setPermisoFechaIngreso(e.target.value)} />
                                </div>
                                <div>
                                    <label>Fecha salida</label>
                                    <input className="form-control" type="date" value={permisoFechaSalida} onChange={(e) => setPermisoFechaSalida(e.target.value)} />
                                </div>
                                <div className="informes-col-span-2">
                                    <label>Correo centro</label>
                                    <input className="form-control" value={permisoCorreoCentro} onChange={(e) => setPermisoCorreoCentro(e.target.value)} />
                                </div>
                                <div>
                                    <label>Telefono centro</label>
                                    <input className="form-control" value={permisoTelefonoCentro} onChange={(e) => setPermisoTelefonoCentro(e.target.value)} />
                                </div>
                                <div>
                                    <label>Base tierra</label>
                                    <select className="form-control" value={permisoBaseTierra} onChange={(e) => setPermisoBaseTierra(e.target.value)}>
                                        <option value="">Seleccionar</option>
                                        <option value="si">Si</option>
                                        <option value="no">No</option>
                                    </select>
                                </div>
                                <div>
                                    <label>Cantidad de radares</label>
                                    <input
                                        className="form-control"
                                        inputMode="numeric"
                                        value={permisoCantidadRadares}
                                        onChange={(e) => setPermisoCantidadRadares(String(e.target.value || "").replace(/\D/g, ""))}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="informes-editor-section">
                        <h6 className="informes-editor-section-title">Equipo tecnico y recepcion</h6>
                        <div className="informes-editor-subsection">
                            <div className="informes-editor-subsection-head">
                                <div>
                                    <strong>Tecnicos del permiso</strong>
                                    <small>Principal, apoyo y tecnicos adicionales.</small>
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={agregarTecnicoAdicionalPermiso}
                                >
                                    <i className="fas fa-user-plus mr-1" />
                                    Agregar tecnico
                                </button>
                            </div>
                            <div className="informes-tecnicos-grid">
                                <div className="informes-tecnico-card">
                                    <div className="informes-tecnico-card-head">
                                        <span className="informes-tecnico-badge">Tecnico 1</span>
                                    </div>
                                    <div>
                                        <label>Nombre</label>
                                        <input className="form-control" value={permisoTecnico1} onChange={(e) => setPermisoTecnico1(e.target.value)} />
                                    </div>
                                    <div>
                                        <label>
                                            Firma tecnico 1 {permisoFirmaTecnico1 ? <span className="firma-check">✓</span> : null}
                                        </label>
                                        {isSignatureImage(permisoFirmaTecnico1) ? (
                                            <div className="signature-preview-box">
                                                <img src={permisoFirmaTecnico1} alt="Firma tecnico 1 permiso" className="signature-preview-img" />
                                            </div>
                                        ) : (
                                            <input className="form-control" value={permisoFirmaTecnico1} onChange={(e) => setPermisoFirmaTecnico1(e.target.value)} />
                                        )}
                                    </div>
                                </div>
                                <div className="informes-tecnico-card">
                                    <div className="informes-tecnico-card-head">
                                        <span className="informes-tecnico-badge">Tecnico 2</span>
                                    </div>
                                    <div>
                                        <label>Nombre</label>
                                        <input className="form-control" value={permisoTecnico2} onChange={(e) => setPermisoTecnico2(e.target.value)} />
                                    </div>
                                    <div>
                                        <label>
                                            Firma tecnico 2 {permisoFirmaTecnico2 ? <span className="firma-check">✓</span> : null}
                                        </label>
                                        {isSignatureImage(permisoFirmaTecnico2) ? (
                                            <div className="signature-preview-box">
                                                <img src={permisoFirmaTecnico2} alt="Firma tecnico 2 permiso" className="signature-preview-img" />
                                            </div>
                                        ) : (
                                            <input className="form-control" value={permisoFirmaTecnico2} onChange={(e) => setPermisoFirmaTecnico2(e.target.value)} />
                                        )}
                                    </div>
                                </div>
                                {permisoTecnicosAdicionales.map((item, index) => (
                                    <div className="informes-tecnico-card informes-tecnico-card-extra" key={`permiso-tec-extra-${index}`}>
                                        <div className="informes-tecnico-card-head">
                                            <span className="informes-tecnico-badge">Tecnico {index + 3}</span>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-link text-danger p-0"
                                                onClick={() => eliminarTecnicoAdicionalPermiso(index)}
                                                title="Quitar tecnico"
                                            >
                                                <i className="fas fa-trash-alt" />
                                            </button>
                                        </div>
                                        <div>
                                            <label>Nombre</label>
                                            <input
                                                className="form-control"
                                                value={item.nombre}
                                                onChange={(e) => actualizarTecnicoAdicionalPermiso(index, "nombre", e.target.value)}
                                            />
                                        </div>
                                        <div>
                                            <label>
                                                Firma tecnico {index + 3} {item.firma ? <span className="firma-check">✓</span> : null}
                                            </label>
                                            {isSignatureImage(item.firma) ? (
                                                <div className="signature-preview-box">
                                                    <img src={item.firma} alt={`Firma tecnico permiso ${index + 3}`} className="signature-preview-img" />
                                                </div>
                                            ) : (
                                                <input
                                                    className="form-control"
                                                    value={item.firma}
                                                    onChange={(e) => actualizarTecnicoAdicionalPermiso(index, "firma", e.target.value)}
                                                />
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="informes-editor-subsection informes-editor-subsection-recepcion">
                            <div className="informes-editor-subsection-head">
                                <div>
                                    <strong>Recepcion</strong>
                                    <small>Persona que recepciona el trabajo en terreno.</small>
                                </div>
                            </div>
                            <div className="informes-tecnicos-grid informes-recepcion-grid">
                                <div className="informes-tecnico-card informes-recepcion-card">
                                    <div>
                                        <label>Recepciona</label>
                                        <input className="form-control" value={permisoRecepcionaNombre} onChange={(e) => setPermisoRecepcionaNombre(e.target.value)} />
                                    </div>
                                    <div>
                                        <label>RUT recepciona</label>
                                        <input className="form-control" value={permisoRecepcionaRut} onChange={(e) => setPermisoRecepcionaRut(e.target.value)} />
                                    </div>
                                    <div>
                                        <label>
                                            Firma recepciona {permisoFirmaRecepciona ? <span className="firma-check">✓</span> : null}
                                        </label>
                                        {isSignatureImage(permisoFirmaRecepciona) ? (
                                            <div className="signature-preview-box">
                                                <img src={permisoFirmaRecepciona} alt="Firma recepciona permiso" className="signature-preview-img" />
                                            </div>
                                        ) : (
                                            <input className="form-control" value={permisoFirmaRecepciona} onChange={(e) => setPermisoFirmaRecepciona(e.target.value)} />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="informes-editor-section">
                        <h6 className="informes-editor-section-title">Puntos GPS</h6>
                        <div className="informes-editor-subsection">
                            <div className="informes-editor-subsection-head">
                                <div>
                                    <strong>Ubicaciones registradas</strong>
                                    <small>Agrega uno o varios puntos para el permiso.</small>
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => setPermisoPuntosGps((prev) => [...prev, { lat: "", lng: "" }])}
                                >
                                    <i className="fas fa-map-marker-alt mr-1" />
                                    Agregar punto
                                </button>
                            </div>
                            <div className="informes-permiso-gps-grid">
                                {permisoPuntosGps.map((punto, idx) => (
                                    <div key={`permiso-gps-${idx}`} className="informes-tecnico-card informes-permiso-gps-card">
                                        <div className="informes-tecnico-card-head">
                                            <span className="informes-tecnico-badge">Punto {idx + 1}</span>
                                            {permisoPuntosGps.length > 1 ? (
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-link text-danger p-0"
                                                    onClick={() => setPermisoPuntosGps((prev) => prev.filter((_, i) => i !== idx))}
                                                    title="Quitar punto"
                                                >
                                                    <i className="fas fa-trash-alt" />
                                                </button>
                                            ) : null}
                                        </div>
                                        <div>
                                            <label>Latitud</label>
                                            <input
                                                className="form-control"
                                                placeholder={`Latitud ${idx + 1}`}
                                                value={punto.lat}
                                                onChange={(e) =>
                                                    setPermisoPuntosGps((prev) =>
                                                        prev.map((it, i) =>
                                                            i === idx ? { ...it, lat: normalizeGpsPointInput(e.target.value) } : it
                                                        )
                                                    )
                                                }
                                            />
                                        </div>
                                        <div>
                                            <label>Longitud</label>
                                            <input
                                                className="form-control"
                                                placeholder={`Longitud ${idx + 1}`}
                                                value={punto.lng}
                                                onChange={(e) =>
                                                    setPermisoPuntosGps((prev) =>
                                                        prev.map((it, i) =>
                                                            i === idx ? { ...it, lng: normalizeGpsPointInput(e.target.value) } : it
                                                        )
                                                    )
                                                }
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="informes-editor-section">
                        <h6 className="informes-editor-section-title">Sellos</h6>
                        <div className="informes-editor-subsection">
                            <div className="informes-editor-subsection-head">
                                <div>
                                    <strong>Registro de sellos</strong>
                                    <small>Ingresa la ubicacion y el numero antiguo o nuevo por sello.</small>
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => setPermisoSellos((prev) => [...prev, { ubicacion: "", numeroAnterior: "", numeroNuevo: "" }])}
                                >
                                    <i className="fas fa-plus mr-1" />
                                    Agregar sello
                                </button>
                            </div>
                            <div className="informes-permiso-gps-grid">
                                {permisoSellos.map((sello, idx) => (
                                    <div key={`permiso-sello-${idx}`} className="informes-tecnico-card informes-permiso-gps-card">
                                        <div className="informes-tecnico-card-head">
                                            <span className="informes-tecnico-badge">Sello {idx + 1}</span>
                                            {permisoSellos.length > 1 ? (
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-link text-danger p-0"
                                                    onClick={() => setPermisoSellos((prev) => prev.filter((_, i) => i !== idx))}
                                                    title="Quitar sello"
                                                >
                                                    <i className="fas fa-trash-alt" />
                                                </button>
                                            ) : null}
                                        </div>
                                        <div>
                                            <label>Ubicacion</label>
                                            <input
                                                className="form-control"
                                                value={sello.ubicacion}
                                                onChange={(e) =>
                                                    setPermisoSellos((prev) =>
                                                        prev.map((item, i) => (i === idx ? { ...item, ubicacion: e.target.value } : item))
                                                    )
                                                }
                                            />
                                        </div>
                                        <div>
                                            <label>Nro antiguo</label>
                                            <input
                                                className="form-control"
                                                value={sello.numeroAnterior}
                                                onChange={(e) =>
                                                    setPermisoSellos((prev) =>
                                                        prev.map((item, i) =>
                                                            i === idx ? { ...item, numeroAnterior: normalizeSelloNumero(e.target.value) } : item
                                                        )
                                                    )
                                                }
                                            />
                                        </div>
                                        <div>
                                            <label>Nro nuevo</label>
                                            <input
                                                className="form-control"
                                                value={sello.numeroNuevo}
                                                onChange={(e) =>
                                                    setPermisoSellos((prev) =>
                                                        prev.map((item, i) =>
                                                            i === idx ? { ...item, numeroNuevo: normalizeSelloNumero(e.target.value) } : item
                                                        )
                                                    )
                                                }
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="informes-editor-section">
                        <h6 className="informes-editor-section-title">Mediciones y detalle</h6>
                        <div className="informes-editor-subsection">
                            <div className="informes-editor-subsection-head">
                                <div>
                                    <strong>Mediciones de energia</strong>
                                    <small>Valores de referencia tomados en terreno.</small>
                                </div>
                            </div>
                            <div className="informes-tecnicos-grid">
                                <div className="informes-tecnico-card">
                                    <div className="informes-tecnico-card-head">
                                        <span className="informes-tecnico-badge">Fase / neutro</span>
                                    </div>
                                    <div>
                                        <label>Medicion</label>
                                        <input
                                            className="form-control"
                                            type="text"
                                            inputMode="decimal"
                                            placeholder="Ej: 220.5"
                                            value={permisoMedicionFaseNeutro}
                                            onChange={(e) => setPermisoMedicionFaseNeutro(normalizeMeasureInput(e.target.value))}
                                        />
                                    </div>
                                </div>
                                <div className="informes-tecnico-card">
                                    <div className="informes-tecnico-card-head">
                                        <span className="informes-tecnico-badge">Neutro / tierra</span>
                                    </div>
                                    <div>
                                        <label>Medicion</label>
                                        <input
                                            className="form-control"
                                            type="text"
                                            inputMode="decimal"
                                            placeholder="Ej: 0.8"
                                            value={permisoMedicionNeutroTierra}
                                            onChange={(e) => setPermisoMedicionNeutroTierra(normalizeMeasureInput(e.target.value))}
                                        />
                                    </div>
                                </div>
                                <div className="informes-tecnico-card">
                                    <div className="informes-tecnico-card-head">
                                        <span className="informes-tecnico-badge">Frecuencia</span>
                                    </div>
                                    <div>
                                        <label>Hertz</label>
                                        <input
                                            className="form-control"
                                            type="text"
                                            inputMode="decimal"
                                            placeholder="Ej: 50.0"
                                            value={permisoHertz}
                                            onChange={(e) => setPermisoHertz(normalizeMeasureInput(e.target.value))}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="informes-editor-subsection">
                            <div className="informes-editor-subsection-head">
                                <div>
                                    <strong>Equipos devueltos a orca</strong>
                                    <small>Equipos marcados en terreno para retorno a bodega.</small>
                                </div>
                            </div>
                            {permisoEquiposDevueltosOrca.length ? (
                                <div className="informes-permiso-devueltos-list">
                                    {permisoEquiposDevueltosOrca.map((equipo, index) => (
                                        <div key={`permiso-devuelto-${index}`} className="informes-info-card">
                                            <small>{`Equipo ${index + 1}`}</small>
                                            <strong>{equipo?.nombre || "Equipo"}</strong>
                                            <span className="informes-permiso-devuelto-serie">{`N Serie: ${equipo?.numero_serie || "-"}`}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="informes-empty py-2 mb-0">No hay equipos devueltos a orca registrados en este permiso.</div>
                            )}
                        </div>
                        {subcategoria === "mantencion" ? (
                            <div className="informes-editor-subsection">
                                <div className="informes-editor-subsection-head">
                                    <div>
                                        <strong>Cambio de equipo</strong>
                                        <small>Resumen de reemplazos registrados en la mantencion.</small>
                                    </div>
                                </div>
                                {permisoCambiosEquipo.length ? (
                                    <div className="informes-permiso-devueltos-list">
                                        {permisoCambiosEquipo.map((cambio, index) => (
                                            <div key={`permiso-cambio-${index}`} className="informes-info-card">
                                                <small>{`Cambio ${index + 1}`}</small>
                                                <strong>{cambio?.equipo || "Equipo"}</strong>
                                                <span className="informes-permiso-devuelto-serie">{`Serie anterior: ${cambio?.serie_anterior || "-"}`}</span>
                                                <span className="informes-permiso-devuelto-serie">{`Serie nueva: ${cambio?.serie_nueva || "-"}`}</span>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="informes-empty py-2 mb-0">Sin cambios de equipo registrados.</div>
                                )}
                            </div>
                        ) : null}
                        {subcategoria === "mantencion" ? (
                            <div className="informes-editor-subsection">
                                <div className="informes-editor-subsection-head">
                                    <div>
                                        <strong>Checklist de equipos</strong>
                                        <small>Equipos revisados desde mobile con su observacion.</small>
                                    </div>
                                </div>
                                {permisoChecklistMantencion.length ? (
                                    <div className="informes-permiso-devueltos-list">
                                        {permisoChecklistMantencion.map((item, index) => (
                                            <div key={`permiso-check-${index}`} className="informes-info-card">
                                                <small>{item.revisado ? "Revisado" : "Pendiente"}</small>
                                                <strong>{item.equipo_nombre || `Equipo ${index + 1}`}</strong>
                                                <span className="informes-permiso-devuelto-serie">{`N Serie: ${item.numero_serie || "-"}`}</span>
                                                <span className="informes-permiso-devuelto-serie">{`Codigo: ${item.codigo || "-"}`}</span>
                                                {item.observacion ? (
                                                    <span className="informes-permiso-devuelto-serie">{`Observacion: ${item.observacion}`}</span>
                                                ) : null}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="informes-empty py-2 mb-0">Sin checklist de equipos registrado.</div>
                                )}
                            </div>
                        ) : null}
                        <div className="informes-editor-subsection">
                            <div className="informes-editor-subsection-head">
                                <div>
                                    <strong>Descripcion del trabajo</strong>
                                    <small>Resumen operativo del permiso.</small>
                                </div>
                            </div>
                            <div className="informes-filtros-grid">
                                <div className="informes-col-span-2">
                                    <textarea
                                        className="form-control"
                                        rows={4}
                                        value={permisoDescripcionTrabajo}
                                        onChange={(e) => setPermisoDescripcionTrabajo(e.target.value)}
                                        placeholder="Detalle del trabajo realizado"
                                    />
                                </div>
                            </div>
                        </div>
                        {subcategoria === "mantencion" ? (
                            <div className="informes-editor-subsection">
                                <div className="informes-editor-subsection-head">
                                    <div>
                                        <strong>Evidencia</strong>
                                        <small>Fotos adjuntas desde la mantencion en mobile. Puedes agregar, reemplazar o eliminar.</small>
                                    </div>
                                    <button
                                        type="button"
                                        className="informes-evidencia-add-btn"
                                        onClick={() => abrirSelectorEvidenciaMantencion()}
                                        disabled={permisoEvidencias.length >= 3}
                                    >
                                        <i className="fas fa-camera" />
                                        <span>Agregar evidencia</span>
                                    </button>
                                </div>
                                <input
                                    ref={permisoEvidenciaInputRef}
                                    type="file"
                                    accept="image/*"
                                    className="d-none"
                                    onChange={handleSeleccionarEvidenciaMantencion}
                                />
                                {permisoEvidencias.length ? (
                                    <div className="informes-levantamiento-fotos">
                                        {permisoEvidencias.map((foto, index) => (
                                            <div key={`permiso-evidencia-${index}`} className="informes-levantamiento-foto">
                                                {String(foto || "").startsWith("data:image") || String(foto || "").startsWith("http") ? (
                                                    <img src={foto} alt={`Evidencia ${index + 1}`} />
                                                ) : (
                                                    <div className="p-3 text-muted small">{foto}</div>
                                                )}
                                                <span>{`Evidencia ${index + 1}`}</span>
                                                <div className="informes-evidencia-actions">
                                                    <button
                                                        type="button"
                                                        className="informes-evidencia-action informes-evidencia-action-edit"
                                                        onClick={() => abrirSelectorEvidenciaMantencion(index)}
                                                    >
                                                        <i className="fas fa-sync-alt" />
                                                        <span>Reemplazar</span>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="informes-evidencia-action informes-evidencia-action-delete"
                                                        onClick={() => handleEliminarEvidenciaMantencion(index)}
                                                    >
                                                        <i className="fas fa-trash-alt" />
                                                        <span>Eliminar</span>
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="informes-empty py-2 mb-0">Sin evidencia adjunta.</div>
                                )}
                            </div>
                        ) : null}
                    </div>

                    <div className="informes-col-span-2 d-flex gap-2 align-items-end informes-permiso-actions">
                        <button className="btn btn-primary" onClick={handleGuardarPermiso} disabled={savingPermiso}>
                            <i className={`mr-2 fas ${savingPermiso ? "fa-spinner fa-spin" : permisoEditandoId ? "fa-save" : "fa-check-circle"}`} />
                            {savingPermiso ? "Guardando..." : permisoEditandoId ? "Actualizar permiso" : "Guardar permiso"}
                        </button>
                        <button className="btn btn-outline-secondary" onClick={resetFormularioPermiso}>
                            Cerrar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderRetiroEditor = () => (
        <div className="informes-editor-overlay">
            <div className="card informes-editor-card">
                <div className="card-header d-flex justify-content-between align-items-center informes-editor-header">
                    <div>
                        <h5 className="mb-0">Editar retiro en terreno</h5>
                        <small className="informes-editor-subtitle">
                            Ajusta fecha, tecnicos, observacion y equipos retirados con su modalidad de retorno.
                        </small>
                    </div>
                    <button className="btn btn-sm btn-outline-secondary informes-editor-close-btn" onClick={resetFormularioPermiso}>
                        <i className="fas fa-times" />
                    </button>
                </div>
                <div className="card-body">
                    <div className="informes-editor-section">
                        <h6 className="informes-editor-section-title">Datos del centro</h6>
                        <div className="informes-info-grid">
                            <div className="informes-info-card">
                                <small>Empresa</small>
                                <strong>{permisoCentroSeleccionadoForm?.cliente || "-"}</strong>
                            </div>
                            <div className="informes-info-card">
                                <small>Centro</small>
                                <strong>{permisoCentroSeleccionadoForm?.nombre || "-"}</strong>
                            </div>
                            <div className="informes-info-card">
                                <small>Codigo ponton</small>
                                <strong>{permisoCentroSeleccionadoForm?.nombre_ponton || "-"}</strong>
                            </div>
                            <div className="informes-info-card">
                                <small>Region</small>
                                <strong>{permisoRegion || "-"}</strong>
                            </div>
                            <div className="informes-info-card">
                                <small>Localidad</small>
                                <strong>{permisoLocalidad || "-"}</strong>
                            </div>
                            <div className="informes-info-card">
                                <small>Telefono centro</small>
                                <strong>{permisoTelefonoCentro || "-"}</strong>
                            </div>
                        </div>
                        <div className="informes-filtros-grid mt-3">
                            <div>
                                <label>Fecha retiro</label>
                                <input className="form-control" type="date" value={permisoFechaIngreso} onChange={(e) => setPermisoFechaIngreso(e.target.value)} />
                            </div>
                            <div>
                                <label>Tipo retiro</label>
                                <select className="form-control" value={retiroTipo} onChange={(e) => setRetiroTipo(e.target.value === "completo" ? "completo" : "parcial")}>
                                    <option value="parcial">Parcial</option>
                                    <option value="completo">Completo</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="informes-editor-section">
                        <h6 className="informes-editor-section-title">Tecnicos y recepcion</h6>
                        <div className="informes-tecnicos-grid">
                            <div className="informes-tecnico-card">
                                <div className="informes-tecnico-card-head">
                                    <span className="informes-tecnico-badge">Tecnico 1</span>
                                </div>
                                <div>
                                    <label>Nombre</label>
                                    <input className="form-control" value={permisoTecnico1} onChange={(e) => setPermisoTecnico1(e.target.value)} />
                                </div>
                            </div>
                            <div className="informes-tecnico-card">
                                <div className="informes-tecnico-card-head">
                                    <span className="informes-tecnico-badge">Tecnico 2</span>
                                </div>
                                <div>
                                    <label>Nombre</label>
                                    <input className="form-control" value={permisoTecnico2} onChange={(e) => setPermisoTecnico2(e.target.value)} />
                                </div>
                            </div>
                            <div className="informes-tecnico-card">
                                <div className="informes-tecnico-card-head">
                                    <span className="informes-tecnico-badge">Recepcion</span>
                                </div>
                                <div>
                                    <label>Nombre</label>
                                    <input className="form-control" value={permisoRecepcionaNombre} onChange={(e) => setPermisoRecepcionaNombre(e.target.value)} />
                                </div>
                                <div>
                                    <label>RUT</label>
                                    <input className="form-control" value={permisoRecepcionaRut} onChange={(e) => setPermisoRecepcionaRut(e.target.value)} />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="informes-editor-section">
                        <h6 className="informes-editor-section-title">Equipos del retiro</h6>
                        {retiroEquiposChecklist.length ? (
                            <div className="informes-permiso-devueltos-list">
                                {retiroEquiposChecklist.map((item, index) => {
                                    const bloqueado = ["recepcionado_bodega", "revision_bodega", "baja_bodega"].includes(
                                        normalizeText(item?.estado_logistico || "")
                                    );
                                    return (
                                        <div key={`retiro-equipo-${index}`} className="informes-info-card">
                                            <small>{item?.codigo || `Equipo ${index + 1}`}</small>
                                            <strong>{item?.equipo_nombre || "-"}</strong>
                                            <span className="informes-permiso-devuelto-serie">{`N Serie: ${item?.numero_serie || "-"}`}</span>
                                            {bloqueado ? <span className="badge badge-success mb-2">Recepcionado en bodega</span> : null}
                                            <div className="d-flex align-items-center mb-2">
                                                <input
                                                    id={`retiro-check-${index}`}
                                                    type="checkbox"
                                                    className="mr-2"
                                                    checked={!!item?.retirado}
                                                    disabled={bloqueado}
                                                    onChange={(e) =>
                                                        setRetiroEquiposChecklist((prev) =>
                                                            prev.map((row, rowIndex) =>
                                                                rowIndex === index ? { ...row, retirado: e.target.checked } : row
                                                            )
                                                        )
                                                    }
                                                />
                                                <label htmlFor={`retiro-check-${index}`} className="mb-0">
                                                    Marcar como retirado
                                                </label>
                                            </div>
                                            <div className="btn-group btn-group-sm d-flex" role="group">
                                                <button
                                                    type="button"
                                                    className={`btn ${normalizeRetiroModalidad(item?.modalidad_retorno) === "por_mano" ? "btn-success" : "btn-outline-secondary"}`}
                                                    disabled={bloqueado || !item?.retirado}
                                                    onClick={() =>
                                                        setRetiroEquiposChecklist((prev) =>
                                                            prev.map((row, rowIndex) =>
                                                                rowIndex === index ? { ...row, modalidad_retorno: "por_mano" } : row
                                                            )
                                                        )
                                                    }>
                                                    Por mano
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`btn ${normalizeRetiroModalidad(item?.modalidad_retorno) === "despacho_orca" ? "btn-success" : "btn-outline-secondary"}`}
                                                    disabled={bloqueado || !item?.retirado}
                                                    onClick={() =>
                                                        setRetiroEquiposChecklist((prev) =>
                                                            prev.map((row, rowIndex) =>
                                                                rowIndex === index ? { ...row, modalidad_retorno: "despacho_orca" } : row
                                                            )
                                                        )
                                                    }>
                                                    Despacho a orca
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="informes-empty py-2 mb-0">No hay equipos cargados en este retiro.</div>
                        )}
                    </div>

                    <div className="informes-editor-section">
                        <h6 className="informes-editor-section-title">Observacion</h6>
                        <textarea
                            className="form-control"
                            rows={4}
                            value={permisoDescripcionTrabajo}
                            onChange={(e) => setPermisoDescripcionTrabajo(e.target.value)}
                            placeholder="Detalle del retiro"
                        />
                    </div>

                    <div className="informes-col-span-2 d-flex gap-2 align-items-end informes-permiso-actions">
                        <button className="btn btn-primary" onClick={handleGuardarRetiro} disabled={savingPermiso}>
                            <i className={`mr-2 fas ${savingPermiso ? "fa-spinner fa-spin" : "fa-save"}`} />
                            {savingPermiso ? "Guardando..." : "Actualizar retiro"}
                        </button>
                        <button className="btn btn-outline-secondary" onClick={resetFormularioPermiso}>
                            Cerrar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    const openPreviewPdf = (title, bodyHtml) => {
        const w = window.open("", "_blank", "width=980,height=760");
        if (!w) {
            alert("No se pudo abrir la vista previa. Habilita ventanas emergentes.");
            return;
        }
        const doc = `
<!doctype html>
<html lang="es">
<head>
  <meta charset="utf-8" />
  <title>${esc(title)}</title>
  <style>
    body { font-family: Arial, sans-serif; color:#0f172a; margin:0; background:#f8fafc; }
    .wrap { max-width: 980px; margin: 20px auto; background:#fff; border:1px solid #dbe7ff; border-radius:10px; overflow:hidden; }
    .head { padding:14px 18px; background:linear-gradient(135deg,#1d4ed8,#2563eb); color:#fff; display:flex; justify-content:space-between; align-items:center; }
    .head h1 { margin:0; font-size:20px; }
    .toolbar { display:flex; gap:8px; }
    .btn { border:0; border-radius:8px; padding:8px 12px; font-weight:700; cursor:pointer; }
    .btn-print { background:#16a34a; color:#fff; }
    .btn-close { background:#e2e8f0; color:#0f172a; }
    .content { padding:16px 18px; }
    .grid { display:grid; grid-template-columns: repeat(2,minmax(0,1fr)); gap:10px; margin-bottom:12px; break-inside:auto; page-break-inside:auto; }
    .field { border:1px solid #e2e8f0; border-radius:8px; padding:8px 10px; background:#fff; break-inside:auto; page-break-inside:auto; }
    .field b { display:block; font-size:11px; text-transform:uppercase; color:#64748b; margin-bottom:4px; letter-spacing:.04em; }
    .wide { grid-column:1 / -1; }
    .sig-box { border:1px solid #e2e8f0; border-radius:8px; padding:8px; background:#fff; min-height:84px; display:flex; align-items:center; justify-content:center; }
    .sig-img { max-width:100%; max-height:76px; object-fit:contain; }
    .evi-box { border:1px solid #e2e8f0; border-radius:8px; padding:10px; background:#fff; min-height:160px; }
    .evi-grid { display:grid; grid-template-columns:1fr; gap:12px; align-items:stretch; }
    .evi-item { display:flex; flex-direction:column; }
    .evi-img { width:100%; max-height:420px; object-fit:contain; border-radius:8px; background:#f8fafc; border:1px solid #dbeafe; }
    .sig-empty, .sig-text { color:#64748b; font-weight:600; font-size:12px; text-align:center; }
    .doc-top { display:flex; justify-content:space-between; align-items:flex-start; gap:16px; margin-bottom:12px; }
    .doc-meta h2 { margin:0 0 6px; color:#1d4ed8; font-size:20px; }
    .doc-meta .meta-line { color:#334155; font-weight:700; margin-bottom:4px; font-size:13px; }
    .orca-logo { border:1px solid #dbe7ff; border-radius:8px; padding:6px; background:#fff; min-width:112px; }
    .orca-row { display:grid; grid-template-columns:repeat(4, 1fr); gap:0; margin-bottom:4px; }
    .orca-cell { width:23px; height:23px; background:#245b98; color:#fff; border-radius:0; display:flex; align-items:center; justify-content:center; font-weight:900; font-size:14px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .orca-sub { text-align:center; color:#245b98; font-size:11px; font-weight:900; letter-spacing:0.14em; text-transform:uppercase; padding-left:1px; }
	    .sec-title { margin: 8px 0 5px; color:#1d4ed8; font-size:11px; font-weight:800; text-transform:uppercase; letter-spacing:.02em; break-after: avoid; page-break-after: avoid; }
	    .page-break-before { break-before: page; page-break-before: always; }
	    @media print { 
      @page { margin: 12mm; }
      body{background:#fff;} 
      .toolbar{display:none;} 
      .wrap{margin:0; border:0; border-radius:0;} 
      .head h1{font-size:15px;}
      .sec-title{font-size:10px; margin:4px 0 3px; break-inside:avoid; page-break-inside:avoid;}
      .field b{font-size:10px;}
      .grid{gap:6px; margin-bottom:8px; break-inside:auto; page-break-inside:auto;}
      .field{padding:6px 8px; font-size:11px; break-inside:auto; page-break-inside:auto;}
	      .orca-cell{background:#245b98 !important; color:#fff !important; -webkit-print-color-adjust: exact; print-color-adjust: exact;}
	      .orca-sub{color:#245b98 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact;}
	      .page-break-before { break-before: page; page-break-before: always; }
	    }
  </style>
</head>
<body>
  <div class="wrap">
    <div class="head">
      <h1>${esc(title)}</h1>
      <div class="toolbar">
        <button class="btn btn-print" onclick="window.print()">Descargar PDF</button>
        <button class="btn btn-close" onclick="window.close()">Cerrar</button>
      </div>
    </div>
    <div class="content">${bodyHtml}</div>
  </div>
</body>
</html>`;
        w.document.open();
        w.document.write(doc);
        w.document.close();
    };

    const verActaPdf = (acta) => {
        const tecnicosAdicionales = getTecnicosAdicionalesActa(acta);
        const body = `
        <div class="doc-top">
          <div class="orca-logo">
            <div class="orca-row">
              <div class="orca-cell">O</div>
              <div class="orca-cell">R</div>
              <div class="orca-cell">C</div>
              <div class="orca-cell">A</div>
            </div>
            <div class="orca-sub">Tecnologias</div>
          </div>
          <div class="doc-meta">
            <div class="meta-line">N°: ${esc(acta.id_acta_entrega || "-")}</div>
            <div class="meta-line">Fecha y hora: ${esc(toDisplayDate(acta.fecha_registro))}, ${esc(toDisplayHour(acta.created_at))}</div>
            <div class="meta-line">Tecnico 1: ${esc(acta.tecnico_1 || "-")}</div>
            ${acta.tecnico_2 ? `<div class="meta-line">Tecnico 2: ${esc(acta.tecnico_2)}</div>` : ""}
            ${tecnicosAdicionales.map((item, index) => `<div class="meta-line">Tecnico ${index + 3}: ${esc(item.nombre)}</div>`).join("")}
            ${acta.recepciona_nombre ? `<div class="meta-line">Recepciona: ${esc(acta.recepciona_nombre)}</div>` : ""}
          </div>
        </div>

        <div class="sec-title">Datos del centro</div>
        <div class="grid">
          <div class="field"><b>Empresa</b>${esc(acta.empresa || acta.cliente || "-")}</div>
          <div class="field"><b>Centro</b>${esc(acta.centro || "-")}</div>
          <div class="field"><b>Codigo ponton</b>${esc(acta.codigo_ponton || "-")}</div>
          <div class="field"><b>Region</b>${esc(acta.region || "-")}</div>
          <div class="field"><b>Localidad</b>${esc(acta.localidad || "-")}</div>
          <div class="field"><b>Centro origen (traslado)</b>${esc(acta.centro_origen_traslado || "-")}</div>
        </div>

        <div class="sec-title">Detalle del acta</div>
        <div class="grid">
          <div class="field wide"><b>Los equipos considerados en este sistema, corresponden a</b>${esc(acta.equipos_considerados || "-")}</div>
        </div>

        <div class="sec-title">Firmas</div>
        <div class="grid">
          <div class="field">
            <b>Firma tecnico 1</b>
            <div class="sig-box">${signatureHtml(acta.firma_tecnico_1)}</div>
            <div style="margin-top:6px;font-size:12px;font-weight:700;color:#334155;text-align:center;">${esc(acta.tecnico_1 || "Sin nombre")}</div>
          </div>
          <div class="field">
            <b>Firma tecnico 2</b>
            <div class="sig-box">${signatureHtml(acta.firma_tecnico_2)}</div>
            <div style="margin-top:6px;font-size:12px;font-weight:700;color:#334155;text-align:center;">${esc(acta.tecnico_2 || "Sin nombre")}</div>
          </div>
          ${tecnicosAdicionales.map((item, index) => `
          <div class="field">
            <b>Firma tecnico ${index + 3}</b>
            <div class="sig-box">${signatureHtml(item.firma)}</div>
            <div style="margin-top:6px;font-size:12px;font-weight:700;color:#334155;text-align:center;">${esc(item.nombre || "Sin nombre")}</div>
          </div>`).join("")}
          <div class="field wide">
            <b>Firma recepciona</b>
            <div class="sig-box">${signatureHtml(acta.firma_recepciona)}</div>
            <div style="margin-top:6px;font-size:12px;font-weight:700;color:#334155;text-align:center;">${esc(acta.recepciona_nombre || "Sin nombre")}</div>
          </div>
        </div>`;
        openPreviewPdf("Acta de Entrega y Recepción", body);
    };

    const verPermisoPdf = (permiso, opts = {}) => {
        const idField = opts.idField || "id_permiso_trabajo";
        const tituloDocumento = opts.tituloDocumento || "Permiso de Trabajo - Informe de Intervencion y Recepcion";
        const incluirResponsabilidad = !!opts.incluirResponsabilidad;
	    const correlativo = String(permiso?.[idField] || "sin-numero");
	    const hasEvidence = !!String(permiso?.evidencia_foto || "").trim();
        const tecnicosAdicionales = getFirmasTecnicosAdicionales(permiso.firmas_tecnicos_adicionales);
        const gpsItems = String(permiso.puntos_gps || "")
            .split("|")
            .map((p) => p.trim())
            .filter(Boolean);
        const gpsHtml = gpsItems.length
            ? gpsItems
                  .map(
                      (p, idx) =>
                          `<div style="padding:6px 8px;border:1px solid #dbeafe;border-radius:8px;background:#f8fbff;margin-bottom:6px;"><b>Punto ${
                              idx + 1
                          }</b>: ${esc(p)}</div>`
                  )
                  .join("")
            : "-";

        let sellosItems = [];
        try {
            const parsed = JSON.parse(String(permiso.sellos || "[]"));
            if (Array.isArray(parsed)) sellosItems = parsed.filter((s) => s && (s.ubicacion || s.numero));
        } catch (_) {
            sellosItems = [];
        }
        const sellosHtml = sellosItems.length
            ? sellosItems
                  .map((s, idx) => {
                      const antiguo = s.numero_anterior || s.numero || "-";
                      const nuevo = s.numero_nuevo || "sin cambio";
                      return `<div style="padding:6px 8px;border:1px solid #dbeafe;border-radius:8px;background:#f8fbff;margin-bottom:6px;"><b>Sello ${
                          idx + 1
                      }</b> - Ubicacion: ${esc(s.ubicacion || "-")} | Antiguo: ${esc(antiguo)} | Nuevo: ${esc(nuevo)}</div>`;
                  })
                  .join("")
            : "-";

        const baseTierraText =
            permiso.base_tierra === true ? "Si" : permiso.base_tierra === false ? "No" : esc(permiso.base_tierra || "-");
        const cambiosEquipo = Array.isArray(permiso.cambios_equipo) ? permiso.cambios_equipo : [];
        const equiposDevueltosOrca = Array.isArray(permiso.armado_equipos)
            ? permiso.armado_equipos.filter((item) => normalizeText(item?.estado_uso || "") === "devuelto_bodega")
            : [];
        const cambiosEquipoHtml = cambiosEquipo.length
            ? cambiosEquipo
                  .map(
                      (c, idx) => `
                        <div style="padding:8px 10px;border:1px solid #dbeafe;border-radius:8px;background:#f8fbff;margin-bottom:8px;">
                          <div style="font-size:11px;font-weight:800;color:#1d4ed8;margin-bottom:4px;">Cambio ${idx + 1}</div>
                          <div><b>Equipo:</b> ${esc(c.equipo || "-")}</div>
                          <div><b>Serie:</b> ${esc(c.serie_anterior || "-")} → ${esc(c.serie_nueva || "-")}</div>
                        </div>
                      `
                  )
                  .join("")
            : `<div class="sig-empty">Sin cambios de equipo registrados</div>`;
        const equiposDevueltosOrcaHtml = equiposDevueltosOrca
            .map(
                (equipo, idx) => `
                    <div style="padding:8px 10px;border:1px solid #dbeafe;border-radius:8px;background:#f8fbff;margin-bottom:8px;">
                      <div style="font-size:11px;font-weight:800;color:#1d4ed8;margin-bottom:4px;">Equipo ${idx + 1}</div>
                      <div><b>Nombre:</b> ${esc(equipo?.nombre || "Equipo")}</div>
                      <div><b>N Serie:</b> ${esc(equipo?.numero_serie || "-")}</div>
                    </div>
                  `
            )
            .join("");
        const equiposDevueltosOrcaSection = equiposDevueltosOrca.length
            ? `
                <div class="sec-title">Equipos devueltos a orca</div>
                <div class="grid">
                  <div class="field wide"><b>Detalle</b>${equiposDevueltosOrcaHtml}</div>
                </div>
              `
            : "";

        const body = `
        <div class="doc-top">
          <div class="orca-logo">
            <div class="orca-row">
              <div class="orca-cell">O</div>
              <div class="orca-cell">R</div>
              <div class="orca-cell">C</div>
              <div class="orca-cell">A</div>
            </div>
            <div class="orca-sub">Tecnologias</div>
          </div>
          <div class="doc-meta">
            <div class="meta-line">N°: ${esc(correlativo)}</div>
            <div class="meta-line">Fecha y hora: ${esc(toDisplayDate(permiso.fecha_ingreso))}, ${esc(toDisplayHour(permiso.created_at))}</div>
            <div class="meta-line">Tecnico 1: ${esc(permiso.tecnico_1 || "-")}</div>
            ${permiso.tecnico_2 ? `<div class="meta-line">Tecnico 2: ${esc(permiso.tecnico_2)}</div>` : ""}
            ${tecnicosAdicionales.map((item, index) => `<div class="meta-line">Tecnico ${index + 3}: ${esc(item.nombre)}</div>`).join("")}
          </div>
        </div>

        <div class="sec-title">Datos del centro</div>
        <div class="grid">
          <div class="field"><b>Empresa</b>${esc(permiso.empresa || permiso.cliente || "-")}</div>
          <div class="field"><b>Centro</b>${esc(permiso.centro || "-")}</div>
          <div class="field"><b>Codigo ponton</b>${esc(permiso.codigo_ponton || "-")}</div>
          <div class="field"><b>Region</b>${esc(permiso.region || "-")}</div>
          <div class="field"><b>Localidad</b>${esc(permiso.localidad || "-")}</div>
        </div>

        <div class="sec-title">Contacto del centro</div>
        <div class="grid">
          <div class="field"><b>Correo centro</b>${esc(permiso.correo_centro || "-")}</div>
          <div class="field"><b>Telefono centro</b>${esc(permiso.telefono_centro || "-")}</div>
        </div>

        <div class="sec-title">Datos operativos</div>
        <div class="grid">
          <div class="field"><b>Base tierra</b>${baseTierraText}</div>
          <div class="field"><b>Cantidad de radares</b>${esc(permiso.cantidad_radares ?? "-")}</div>
          ${incluirResponsabilidad ? `<div class="field"><b>Responsabilidad</b>${esc(permiso.responsabilidad || "-")}</div>` : ""}
        </div>

        <div class="sec-title">Fechas de intervencion</div>
        <div class="grid">
          <div class="field"><b>Fecha ingreso</b>${esc(toDisplayDate(permiso.fecha_ingreso))}</div>
          <div class="field"><b>Fecha salida</b>${esc(toDisplayDate(permiso.fecha_salida))}</div>
        </div>

        <div class="sec-title">Puntos GPS</div>
        <div class="grid">
          <div class="field wide"><b>Detalle</b>${gpsHtml}</div>
        </div>

        <div class="sec-title">Mediciones de energia</div>
        <div class="grid">
          <div class="field"><b>Medicion fase/neutro</b>${esc(permiso.medicion_fase_neutro || "-")}</div>
          <div class="field"><b>Medicion neutro/tierra</b>${esc(permiso.medicion_neutro_tierra || "-")}</div>
          <div class="field"><b>Hertz</b>${esc(permiso.hertz || "-")}</div>
        </div>

        <div class="sec-title">Sellos</div>
        <div class="grid">
          <div class="field wide"><b>Detalle</b>${sellosHtml}</div>
        </div>

        ${equiposDevueltosOrcaSection}

        <div class="sec-title">Descripcion del trabajo</div>
        <div class="grid">
          <div class="field wide"><b>Descripcion del trabajo</b>${esc(permiso.descripcion_trabajo || "-")}</div>
        </div>

        ${
          incluirResponsabilidad
            ? `<div class="sec-title">Cambio de equipo</div>
        <div class="grid">
          <div class="field wide">${cambiosEquipoHtml}</div>
        </div>`
            : ""
        }

        <div class="sec-title">Cliente</div>
        <div class="grid">
          <div class="field"><b>Recepciona</b>${esc(permiso.recepciona_nombre || "-")}</div>
          <div class="field"><b>RUT recepciona</b>${esc(permiso.recepciona_rut || "-")}</div>
        </div>

        <div class="sec-title">Firmas</div>
        <div class="grid">
          <div class="field">
            <b>Firma tecnico 1</b>
            <div class="sig-box">${signatureHtml(permiso.firma_tecnico_1)}</div>
            <div style="margin-top:6px;font-size:12px;font-weight:700;color:#334155;text-align:center;">${esc(permiso.tecnico_1 || "Sin nombre")}</div>
          </div>
          <div class="field">
            <b>Firma tecnico 2</b>
            <div class="sig-box">${signatureHtml(permiso.firma_tecnico_2)}</div>
            <div style="margin-top:6px;font-size:12px;font-weight:700;color:#334155;text-align:center;">${esc(permiso.tecnico_2 || "Sin nombre")}</div>
          </div>
          ${tecnicosAdicionales.map((item, index) => `
          <div class="field">
            <b>Firma tecnico ${index + 3}</b>
            <div class="sig-box">${signatureHtml(item.firma)}</div>
            <div style="margin-top:6px;font-size:12px;font-weight:700;color:#334155;text-align:center;">${esc(item.nombre || "Sin nombre")}</div>
          </div>`).join("")}
          <div class="field wide">
            <b>Firma recepciona</b>
            <div class="sig-box">${signatureHtml(permiso.firma_recepciona)}</div>
            <div style="margin-top:6px;font-size:12px;font-weight:700;color:#334155;text-align:center;">${esc(permiso.recepciona_nombre || "Sin nombre")}</div>
          </div>
        </div>
	        ${
	          hasEvidence
	            ? `<div class="page-break-before">
	        <div class="sec-title">Evidencia</div>
	        <div class="grid">
	          <div class="field wide">
	            <div class="evi-box">${evidenceHtml(permiso.evidencia_foto)}</div>
	          </div>
	        </div>
	        </div>`
	            : ""
	        }`;
        const centroNombre = String(permiso.centro || "sin-centro").trim();
        openPreviewPdf(
            `${correlativo} - ${tituloDocumento} ${centroNombre}`,
            body
        );
    };

    const verPermisoDesdeActaPdf = async (acta) => {
        const centroId = Number(acta?.centro_id || 0);
        let permiso = permisosTrabajo.find((p) => Number(p.centro_id || 0) === centroId);
        if (!permiso && centroId) {
            try {
                const data = await obtenerPermisosTrabajo({ centro_id: centroId });
                const lista = Array.isArray(data) ? data : [];
                permiso = lista[0] || null;
                if (lista.length) {
                    setPermisosTrabajo((prev) => {
                        const ids = new Set(prev.map((x) => Number(x.id_permiso_trabajo || 0)));
                        const extra = lista.filter((x) => !ids.has(Number(x.id_permiso_trabajo || 0)));
                        return extra.length ? [...prev, ...extra] : prev;
                    });
                }
            } catch (error) {
                console.error("Error al cargar permiso para vista previa:", error);
            }
        }
        if (!permiso) {
            alert("Este centro aun no tiene permiso de trabajo registrado.");
            return;
        }
        verPermisoPdf(permiso);
    };

    const verMantencionPdf = async (mantencion) => {
        const mantencionId = Number(mantencion?.id_mantencion_terreno || 0);
        if (!mantencionId) {
            alert("Mantencion invalida para vista previa.");
            return;
        }
        let cambios = [];
        try {
            const data = await obtenerCambiosEquipoMantencion(mantencionId);
            cambios = Array.isArray(data) ? data : [];
        } catch (error) {
            console.error("Error al cargar cambios de equipo para vista previa:", error);
        }
        verPermisoPdf(
            { ...mantencion, cambios_equipo: cambios },
            {
                idField: "id_mantencion_terreno",
                tituloDocumento: "Mantencion en Terreno - Informe",
                incluirResponsabilidad: true
            }
        );
    };

    const verRetiroPdf = (retiro) => {
        const retiroAsPermiso = {
            ...retiro,
            fecha_ingreso: retiro.fecha_retiro || retiro.fecha_ingreso,
            fecha_salida: null,
            descripcion_trabajo: retiro.observacion || retiro.descripcion_trabajo || "",
            puntos_gps: retiro.puntos_gps || "",
            medicion_fase_neutro: retiro.medicion_fase_neutro || "",
            medicion_neutro_tierra: retiro.medicion_neutro_tierra || "",
            hertz: retiro.hertz || "",
            tecnico_1: retiro.tecnico_1 || "",
            tecnico_2: retiro.tecnico_2 || "",
            recepciona_nombre: retiro.recepciona_nombre || "",
            recepciona_rut: retiro.recepciona_rut || "",
            firma_tecnico_1: retiro.firma_tecnico_1 || "",
            firma_tecnico_2: retiro.firma_tecnico_2 || "",
            firma_recepciona: retiro.firma_recepciona || "",
            tipo_retiro: retiro.tipo_retiro || "",
            estado_logistico: retiro.estado_logistico || "",
        };
        verPermisoPdf(retiroAsPermiso, {
            idField: "id_retiro_terreno",
            tituloDocumento: "Retiro en Terreno - Informe",
            incluirResponsabilidad: false
        });
    };

    const verLevantamientoPdf = (levantamiento) => {
        const fotos = getLevantamientoFotos(levantamiento?.fotos);
        const fotosHtml = fotos.length
            ? fotos
                .map((foto, idx) => {
                    const uri = String(foto?.uri || "").trim();
                    if (!uri) return "";
                    const descripcion = esc(foto?.descripcion || `Foto ${idx + 1}`);
                    return `
                    <div style="margin-bottom:14px;">
                      <div style="font-size:12px;font-weight:700;color:#334155;margin-bottom:6px;">${descripcion}</div>
                      <img src="${uri}" alt="${descripcion}" style="width:100%;max-height:320px;object-fit:contain;border:1px solid #dbeafe;border-radius:8px;background:#f8fafc;" />
                    </div>`;
                })
                .join("")
            : `<div class="sig-empty">Sin fotos registradas</div>`;

        const body = `
        <div class="doc-top">
          <div class="orca-logo">
            <div class="orca-row">
              <div class="orca-cell">O</div>
              <div class="orca-cell">R</div>
              <div class="orca-cell">C</div>
              <div class="orca-cell">A</div>
            </div>
            <div class="orca-sub">Tecnologias</div>
          </div>
          <div class="doc-meta">
            <div class="meta-line">N°: ${esc(levantamiento.id_levantamiento_terreno || "-")}</div>
            <div class="meta-line">Fecha levantamiento: ${esc(toDisplayDate(levantamiento.fecha_levantamiento))}</div>
            <div class="meta-line">Fecha y hora registro: ${esc(toDisplayDate(levantamiento.created_at))}, ${esc(toDisplayHour(levantamiento.created_at))}</div>
          </div>
        </div>

        <div class="sec-title">Datos del centro</div>
        <div class="grid">
          <div class="field"><b>Empresa</b>${esc(levantamiento.empresa || levantamiento.cliente || "-")}</div>
          <div class="field"><b>Centro</b>${esc(levantamiento.centro || "-")}</div>
          <div class="field"><b>Codigo ponton</b>${esc(levantamiento.codigo_ponton || "-")}</div>
          <div class="field"><b>Region</b>${esc(levantamiento.region || "-")}</div>
          <div class="field"><b>Localidad</b>${esc(levantamiento.localidad || "-")}</div>
          <div class="field"><b>Estado</b>${esc(levantamiento.estado || "-")}</div>
        </div>

        <div class="sec-title">Levantamiento</div>
        <div class="grid">
          <div class="field wide"><b>Resumen</b>${esc(levantamiento.resumen || "-")}</div>
          <div class="field wide"><b>Observaciones</b>${esc(levantamiento.observaciones || "-")}</div>
        </div>

        <div class="sec-title">Mediciones de energia</div>
        <div class="grid">
          <div class="field"><b>Voltaje</b>${esc(formatMeasureWithUnit(levantamiento.medicion_voltaje, "V"))}</div>
          <div class="field"><b>Corriente</b>${esc(formatMeasureWithUnit(levantamiento.medicion_corriente, "A"))}</div>
          <div class="field"><b>Potencia</b>${esc(formatMeasureWithUnit(levantamiento.medicion_potencia, "W"))}</div>
        </div>

        <div class="sec-title">Evidencia fotografica</div>
        <div class="grid">
          <div class="field wide">
            <div class="evi-box">${fotosHtml}</div>
          </div>
        </div>`;
        openPreviewPdf("Levantamiento en Terreno - Informe", body);
    };

    const verArmadoDesdeActa = async (acta) => {
        const centroId = Number(acta?.centro_id || 0);
        if (!centroId) {
            alert("Esta acta no tiene centro valido para buscar armados.");
            return;
        }
        try {
            const [armadosData, equiposData] = await Promise.all([
                obtenerArmados({ centro_id: centroId, estado: "finalizado" }),
                obtenerEquipos(centroId)
            ]);
            const lista = Array.isArray(armadosData) ? armadosData : [];
            if (!lista.length) {
                alert("Este centro no tiene armados finalizados.");
                return;
            }
            const armadoVinculadoId = Number(acta?.armado_id || 0);
            const armadoVinculado = armadoVinculadoId
                ? lista.find((x) => Number(x?.id_armado || 0) === armadoVinculadoId)
                : null;
            const armado = armadoVinculado || lista
                .slice()
                .sort((a, b) => new Date(b?.fecha_cierre || 0).getTime() - new Date(a?.fecha_cierre || 0).getTime())[0];
            const armadoId = Number(armado?.id_armado || 0);
            const materialesData = armadoId ? await obtenerMaterialesArmado(armadoId) : [];
            const equipos = Array.isArray(equiposData) ? equiposData : [];
            const materiales = Array.isArray(materialesData) ? materialesData : [];
            const equiposConIdx = equipos.map((e, idx) => ({ ...e, __idx: idx }));
            const usados = new Set();
            const filasEquiposPartes = [];
            const pushBloque = (titulo, lista) => {
                filasEquiposPartes.push(`
                  <tr style="background:#1e3a8a;color:#ffffff;">
                    <td colspan="4" style="padding:6px 8px;border:1px solid #e2e8f0;font-weight:800;text-transform:uppercase;font-size:11.5px;">${esc(titulo)}</td>
                  </tr>
                `);
                if (!lista.length) {
                    filasEquiposPartes.push(`
                      <tr><td colspan="4" style="text-align:center;color:#64748b;padding:6px 8px;border:1px solid #e2e8f0;font-size:11.5px;">Sin registros</td></tr>
                    `);
                    return;
                }
                lista.forEach((e) => {
                    filasEquiposPartes.push(`
                      <tr>
                        <td style="padding:6px 8px;border:1px solid #e2e8f0;font-size:11.5px;">${esc(e?.nombre || "-")}</td>
                        <td style="padding:6px 8px;border:1px solid #e2e8f0;font-size:11.5px;">${esc(e?.caja || "-")}</td>
                        <td style="padding:6px 8px;border:1px solid #e2e8f0;font-size:11.5px;">${esc(e?.numero_serie || e?.nro_serie || "-")}</td>
                        <td style="padding:6px 8px;border:1px solid #e2e8f0;font-size:11.5px;">${esc(e?.codigo || "-")}</td>
                      </tr>
                    `);
                });
            };
            GRUPOS_EQUIPOS_ARMADO.forEach((grupo) => {
                const itemsNorm = (grupo.items || []).map((n) => normalizarNombreEquipo(n));
                const presentes = equiposConIdx.filter((e) => {
                    if (usados.has(e.__idx)) return false;
                    return itemsNorm.includes(normalizarNombreEquipo(e?.nombre || ""));
                });
                presentes.forEach((e) => usados.add(e.__idx));
                pushBloque(grupo.titulo, presentes);
            });
            const extras = equiposConIdx.filter((e) => !usados.has(e.__idx));
            pushBloque("Otros", extras);
            const filasEquipos = filasEquiposPartes.join("");
            const body = `
              <div class="doc-top">
                <div class="orca-logo">
                  <div class="orca-row">
                    <div class="orca-cell">O</div>
                    <div class="orca-cell">R</div>
                    <div class="orca-cell">C</div>
                    <div class="orca-cell">A</div>
                  </div>
                  <div class="orca-sub">Tecnologias</div>
                </div>
                <div class="doc-meta">
                  <div class="meta-line">N°: ${esc(armado?.id_armado || "-")}</div>
                  <div class="meta-line">Tecnico: ${esc(armado?.tecnico?.nombre || "-")}</div>
                  <div class="meta-line">Fecha cierre: ${esc(toDisplayDate(armado?.fecha_cierre))}</div>
                </div>
              </div>

              <div class="sec-title">Resumen de armado</div>
              <div class="grid">
                <div class="field"><b>Centro</b>${esc(armado?.centro?.nombre || acta?.centro || "-")}</div>
                <div class="field"><b>Cliente</b>${esc(armado?.centro?.cliente || acta?.empresa || acta?.cliente || "-")}</div>
                <div class="field"><b>Estado</b>${esc(armado?.estado || "-")}</div>
                <div class="field"><b>Total cajas</b>${esc(armado?.total_cajas ?? "-")}</div>
                <div class="field"><b>Asignado</b>${esc(toDisplayDate(armado?.fecha_asignacion))}</div>
                <div class="field"><b>Inicio armado</b>${esc(toDisplayDate(armado?.fecha_inicio))}</div>
                <div class="field wide"><b>Observacion</b>${esc(armado?.observacion || "-")}</div>
              </div>

              <div class="sec-title">Equipos</div>
              <div class="field wide" style="padding:0;">
                <table style="width:100%;border-collapse:collapse;font-size:12px;">
                  <thead>
                    <tr style="background:#eff6ff;">
                      <th style="text-align:left;padding:6px 8px;border:1px solid #e2e8f0;">Equipo</th>
                      <th style="text-align:left;padding:6px 8px;border:1px solid #e2e8f0;">Caja</th>
                      <th style="text-align:left;padding:6px 8px;border:1px solid #e2e8f0;">Nro Serie</th>
                      <th style="text-align:left;padding:6px 8px;border:1px solid #e2e8f0;">Codigo</th>
                    </tr>
                  </thead>
                  <tbody>${filasEquipos}</tbody>
                </table>
              </div>
            `;
            openPreviewPdf(
                `${armado?.id_armado || "-"} - Informe de Armado ${armado?.centro?.nombre || acta?.centro || ""}`,
                body
            );
        } catch (error) {
            console.error("Error al cargar armado desde acta:", error);
            alert("No se pudo abrir el armado asociado.");
        }
    };

    const handleAbrirRegistroReciente = (item) => {
        if (!item?.raw) return;
        if (item.tipo === "acta_entrega" || item.tipo === "reapuntamiento") {
            setSubcategoria(item.tipo);
            handleEditarActa(item.raw);
            return;
        }
        if (item.tipo === "intervencion") {
            setSubcategoria("intervencion");
            handleEditarPermiso(item.raw);
            return;
        }
        if (item.tipo === "mantencion") {
            setSubcategoria("mantencion");
            verMantencionPdf(item.raw);
            return;
        }
        if (item.tipo === "retiro") {
            setSubcategoria("retiro");
            verRetiroPdf(item.raw);
            return;
        }
        if (item.tipo === "levantamiento") {
            setSubcategoria("levantamiento");
            verLevantamientoPdf(item.raw);
        }
    };

    return (
        <div className="informes-centros-page container-fluid">
            <div className="card informes-centros-hero">
                <div className="informes-hero-content">
                    <span className="informes-hero-icon">
                        <i className="fas fa-chart-pie" />
                    </span>
                    <div>
                        <p className="informes-kicker">Reporteria</p>
                        <h2>Informes de centros</h2>
                        <p className="informes-subtitle">Instalacion, permisos de trabajo, mantenciones y retiros por centro.</p>
                    </div>
                </div>
            </div>

            <div className="card informes-centros-filtros">
                <div className="card-body informes-filtros-grid">
                    <div>
                        <label>Subcategoria</label>
                        <select className="form-control" value={subcategoria} onChange={(e) => setSubcategoria(e.target.value)}>
                            {SUBCATEGORIAS.map((item) => (
                                <option key={item.value} value={item.value}>
                                    {item.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label>Cliente</label>
                        <select className="form-control" value={filtroCliente} onChange={(e) => setFiltroCliente(e.target.value)}>
                            <option value="all">Todos los clientes</option>
                            {clientes.map((cliente) => (
                                <option key={cliente} value={cliente}>
                                    {cliente}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label>Centro</label>
                        <select className="form-control" value={filtroCentroId} onChange={(e) => setFiltroCentroId(e.target.value)}>
                            <option value="">Todos los centros</option>
                            {centrosFiltroCliente.map((centro) => (
                                <option key={centro.id || centro.id_centro} value={centro.id || centro.id_centro}>
                                    {centro.nombre}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label>Fecha desde</label>
                        <input className="form-control" type="date" value={filtroFechaDesde} onChange={(e) => setFiltroFechaDesde(e.target.value)} />
                    </div>
                    <div>
                        <label>Fecha hasta</label>
                        <input className="form-control" type="date" value={filtroFechaHasta} onChange={(e) => setFiltroFechaHasta(e.target.value)} />
                    </div>
                    {(subcategoria === "intervencion" || subcategoria === "mantencion" || subcategoria === "retiro" || subcategoria === "levantamiento") ? (
                        <div>
                            <label>Correlativo</label>
                            <input
                                className="form-control"
                                placeholder="Ej: N128 o 128"
                                value={filtroCorrelativo}
                                onChange={(e) => setFiltroCorrelativo(e.target.value)}
                            />
                        </div>
                    ) : null}
                </div>
            </div>

            <div className="card informes-overview-card">
                <div className="card-body">
                    <div className="informes-overview-head">
                        <div>
                            <h5 className="mb-1">Actividad reciente</h5>
                            <p className="text-muted mb-0">Vista consolidada para revisar lo ultimo registrado sin entrar a cada subcategoria.</p>
                        </div>
                        <div className="informes-overview-quickstats">
                            <div className="informes-overview-pill">
                                <small>Este mes</small>
                                <strong>{overviewTotalesRapidos.esteMes}</strong>
                            </div>
                            <div className="informes-overview-pill">
                                <small>Ultimos 30 dias</small>
                                <strong>{overviewTotalesRapidos.ultimos30}</strong>
                            </div>
                            <div className="informes-overview-pill">
                                <small>Centros</small>
                                <strong>{overviewCentrosIntervenidos}</strong>
                            </div>
                        </div>
                    </div>

                    <div className="informes-overview-periods">
                        {OVERVIEW_PERIODS.map((periodo) => (
                            <button
                                key={periodo.value}
                                type="button"
                                className={`btn btn-sm ${overviewPeriodo === periodo.value ? "btn-primary" : "btn-outline-primary"}`}
                                onClick={() => setOverviewPeriodo(periodo.value)}>
                                {periodo.label}
                            </button>
                        ))}
                    </div>

                    <div className="informes-overview-grid">
                        {overviewResumenCards.map((card) => (
                            <div key={card.key} className={`informes-overview-metric informes-overview-${card.tone}`}>
                                <div className="informes-overview-metric-icon">
                                    <i className={`fas ${card.icon}`} />
                                </div>
                                <div>
                                    <small>{card.label}</small>
                                    <strong>{card.value}</strong>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="informes-overview-table-wrap">
                        {overviewLoading ? (
                            <div className="informes-empty">Cargando actividad reciente...</div>
                        ) : !overviewRowsRecientes.length ? (
                            <div className="informes-empty">No hay actividad reciente para el periodo seleccionado.</div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-sm table-hover informes-table informes-overview-table mb-0">
                                    <thead>
                                        <tr>
                                            <th>Fecha</th>
                                            <th>Tipo</th>
                                            <th>Correlativo</th>
                                            <th>Cliente</th>
                                            <th>Centro</th>
                                            <th>Tecnicos</th>
                                            <th>Estado</th>
                                            <th>Resumen</th>
                                            <th>Accion</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {overviewRowsRecientes.map((item) => (
                                            <tr key={item.id}>
                                                <td>{toDisplayDate(item.fechaPrincipal)}</td>
                                                <td>
                                                    <span className={`informes-overview-type type-${item.tipo}`}>{item.tipoLabel}</span>
                                                </td>
                                                <td>{item.correlativo}</td>
                                                <td>{item.cliente || "-"}</td>
                                                <td>{item.centro || "-"}</td>
                                                <td className="equipos-col">{item.tecnico || "-"}</td>
                                                <td>{item.estado || "-"}</td>
                                                <td className="equipos-col">{item.descripcion || "-"}</td>
                                                <td className="acciones-col">
                                                    <button className="btn btn-sm btn-outline-primary" onClick={() => handleAbrirRegistroReciente(item)}>
                                                        <i className="fas fa-arrow-right mr-1" />
                                                        Abrir
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {(subcategoria === "acta_entrega" || subcategoria === "reapuntamiento") ? (
                <>
                    <div className="card informes-centros-filtros mb-3">
                        <div className="card-body d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <div>
                                <h5 className="mb-1">
                                    {subcategoria === "reapuntamiento" ? "Reapuntamiento" : "Actas de entrega"}
                                </h5>
                                <p className="text-muted mb-0">
                                    Selecciona una fila para abrir el informe y completar datos.
                                </p>
                            </div>
                            <button className="btn btn-primary" onClick={abrirNuevaActa}>
                                <i className="fas fa-plus mr-2" />
                                {subcategoria === "reapuntamiento" ? "Nuevo reapuntamiento" : "Nueva acta"}
                            </button>
                        </div>
                    </div>

                    <div className="card informes-centros-tabla">
                        <div className="card-body">
                            {loadingCentros || loadingActas ? (
                                <div className="informes-empty">Cargando informacion...</div>
                            ) : !actasFiltradas.length ? (
                                <div className="informes-empty">
                                    {subcategoria === "reapuntamiento"
                                        ? "No hay reapuntamientos para los filtros seleccionados."
                                        : "No hay actas de entrega para los filtros seleccionados."}
                                </div>
                            ) : (
                                <>
                                    <div className="table-responsive">
                                        <table className="table table-sm table-hover informes-table mb-0">
                                            <thead>
                                                <tr>
                                                    <th>N</th>
                                                    <th>Empresa</th>
                                                    <th>Region / Localidad</th>
                                                    <th>Centro</th>
                                                    <th>Codigo Ponton</th>
                                                    <th>Fecha registro</th>
                                                    <th>Tecnico 1</th>
                                                    <th>Tecnico 2</th>
                                                    <th>Tecnico 3</th>
                                                    <th>Recepciona</th>
                                                    <th>Equipos considerados en este sistema</th>
                                                    <th>Centro origen (traslado)</th>
                                                    <th>Acciones</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {actasPaginadas.map((acta, index) => (
                                                    <tr key={acta.id_acta_entrega}>
                                                        <td>{(actaPage - 1) * actaPageSize + index + 1}</td>
                                                        <td>{acta.empresa || "-"}</td>
                                                        <td>
                                                            <div>{acta.region || "-"}</div>
                                                            <small className="text-muted">{acta.localidad || "-"}</small>
                                                        </td>
                                                        <td>{acta.centro || "-"}</td>
                                                        <td>{acta.codigo_ponton || "-"}</td>
                                                        <td>{toDisplayDate(acta.fecha_registro)}</td>
                                                        <td>{acta.tecnico_1 || "-"}</td>
                                                        <td>{acta.tecnico_2 || "-"}</td>
                                                        <td>{getTecnicosAdicionalesActa(acta).map((item) => item.nombre).join(", ") || "-"}</td>
                                                        <td>{acta.recepciona_nombre || "-"}</td>
                                                        <td className="equipos-col">{acta.equipos_considerados || "-"}</td>
                                                        <td>{acta.centro_origen_traslado || "-"}</td>
                                                        <td className="acciones-col">
                                                            <button className="btn btn-sm btn-outline-primary mr-2" onClick={() => handleEditarActa(acta)}>
                                                                <i className="fas fa-folder-open mr-1" />
                                                                Acta
                                                            </button>
                                                            <button className="btn btn-sm btn-outline-info mr-2" onClick={() => handleAbrirPermisoDesdeActa(acta)}>
                                                                <i className="fas fa-clipboard-list mr-1" />
                                                                Permiso
                                                            </button>
                                                            <button className="btn btn-sm btn-outline-secondary mr-2" onClick={() => verActaPdf(acta)}>
                                                                <i className="fas fa-file-pdf mr-1" />
                                                                Acta
                                                            </button>
                                                            <button className="btn btn-sm btn-outline-secondary mr-2" onClick={() => verPermisoDesdeActaPdf(acta)}>
                                                                <i className="fas fa-file-pdf mr-1" />
                                                                Permiso
                                                            </button>
                                                            <button className="btn btn-sm btn-outline-success mr-2" onClick={() => verArmadoDesdeActa(acta)}>
                                                                <i className="fas fa-tools mr-1" />
                                                                Armado
                                                            </button>
                                                            <button className="btn btn-sm btn-outline-danger" onClick={() => handleEliminarActa(acta.id_acta_entrega)}>
                                                                <i className="fas fa-trash-alt" />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="informes-pagination-bar">
                                        <div className="informes-pagination-left">
                                            <span className="informes-pagination-label">
                                                <i className="fas fa-list-ul" /> Ver
                                            </span>
                                            <select
                                                className="form-control form-control-sm informes-page-size-select"
                                                value={actaPageSize}
                                                onChange={(e) => setActaPageSize(Number(e.target.value) || 10)}>
                                                <option value={10}>10</option>
                                                <option value={15}>15</option>
                                                <option value={30}>30</option>
                                            </select>
                                            <span className="informes-pagination-label">registros</span>
                                        </div>
                                        <small className="informes-pagination-count">
                                            Mostrando {(actaPage - 1) * actaPageSize + 1}-{Math.min(actaPage * actaPageSize, actasFiltradas.length)} de {actasFiltradas.length}
                                        </small>
                                        <div className="informes-pagination-right">
                                            <button
                                                className="btn btn-sm btn-outline-secondary informes-page-btn"
                                                disabled={actaPage <= 1}
                                                onClick={() => setActaPage((p) => Math.max(1, p - 1))}>
                                                <i className="fas fa-chevron-left" /> Anterior
                                            </button>
                                            <span className="informes-page-indicator">
                                                Pagina {actaPage} / {totalActaPages}
                                            </span>
                                            <button
                                                className="btn btn-sm btn-outline-secondary informes-page-btn"
                                                disabled={actaPage >= totalActaPages}
                                                onClick={() => setActaPage((p) => Math.min(totalActaPages, p + 1))}>
                                                Siguiente <i className="fas fa-chevron-right" />
                                            </button>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {mostrarEditorActa ? (
                        <div className="informes-editor-overlay">
                            <div className="card informes-editor-card">
                                <div className="card-header d-flex justify-content-between align-items-center informes-editor-header">
                                    <div>
                                        <h5 className="mb-0">{editandoId ? "Editar acta de entrega" : "Nueva acta de entrega"}</h5>
                                        <small className="informes-editor-subtitle">Completa los datos del centro, equipo tecnico y recepcion.</small>
                                    </div>
                                    <button className="btn btn-sm btn-outline-secondary informes-editor-close-btn" onClick={resetFormulario}>
                                        <i className="fas fa-times" />
                                    </button>
                                </div>
                                <div className="card-body">
                                    <div className="informes-editor-section">
                                        <h6 className="informes-editor-section-title">Datos del centro</h6>
                                        <div className="informes-editor-subsection">
                                            <div className="informes-editor-subsection-head">
                                                <div>
                                                    <strong>Informacion del centro</strong>
                                                    <small>Datos de referencia cargados automaticamente.</small>
                                                </div>
                                            </div>
                                            <div className="informes-info-grid">
                                                <div className="informes-info-card">
                                                    <small>Empresa</small>
                                                    <strong>{centroSeleccionadoForm?.cliente || "-"}</strong>
                                                </div>
                                                <div className="informes-info-card">
                                                    <small>Centro</small>
                                                    <strong>{centroSeleccionadoForm?.nombre || "-"}</strong>
                                                </div>
                                                <div className="informes-info-card">
                                                    <small>Codigo ponton</small>
                                                    <strong>{centroSeleccionadoForm?.nombre_ponton || "-"}</strong>
                                                </div>
                                                <div className="informes-info-card">
                                                    <small>Region</small>
                                                    <strong>{region || "-"}</strong>
                                                </div>
                                                <div className="informes-info-card">
                                                    <small>Localidad</small>
                                                    <strong>{localidad || "-"}</strong>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="informes-editor-subsection">
                                            <div className="informes-editor-subsection-head">
                                                <div>
                                                    <strong>Seleccion y registro</strong>
                                                    <small>Campos editables del acta.</small>
                                                </div>
                                            </div>
                                            <div className="informes-filtros-grid">
                                                <div>
                                                    <label>Cliente</label>
                                                    <select className="form-control" value={clienteIdForm} onChange={(e) => setClienteIdForm(e.target.value)}>
                                                        <option value="">Seleccionar cliente</option>
                                                        {clientesForm.map((cliente) => (
                                                            <option key={cliente.key} value={cliente.key}>
                                                                {cliente.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div>
                                                    <label>Centro (acta)</label>
                                                    <select className="form-control" value={centroIdForm} onChange={(e) => setCentroIdForm(e.target.value)}>
                                                        <option value="">Seleccionar centro</option>
                                                        {centrosFormFiltrados.map((centro) => (
                                                            <option key={centro.id || centro.id_centro} value={centro.id || centro.id_centro}>
                                                                {centro.nombre}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div>
                                                    <label>Fecha de registro</label>
                                                    <input className="form-control" type="date" value={fechaRegistro} onChange={(e) => setFechaRegistro(e.target.value)} />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="informes-editor-section">
                                        <h6 className="informes-editor-section-title">Equipo tecnico y recepcion</h6>
                                        <div className="informes-editor-subsection">
                                            <div className="informes-editor-subsection-head">
                                                <div>
                                                    <strong>Tecnicos del acta</strong>
                                                    <small>Principal, apoyo y tecnicos adicionales.</small>
                                                </div>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-primary"
                                                    onClick={agregarTecnicoAdicionalActa}
                                                >
                                                    <i className="fas fa-user-plus mr-1" />
                                                    Agregar tecnico
                                                </button>
                                            </div>
                                            <div className="informes-tecnicos-grid">
                                                <div className="informes-tecnico-card">
                                                    <div className="informes-tecnico-card-head">
                                                        <span className="informes-tecnico-badge">Tecnico 1</span>
                                                    </div>
                                                    <div>
                                                        <label>Nombre</label>
                                                        <input className="form-control" value={tecnico1} onChange={(e) => setTecnico1(e.target.value)} />
                                                    </div>
                                                    <div>
                                                        <label>
                                                            Firma tecnico 1 {firmaTecnico1 ? <span className="firma-check">✓</span> : null}
                                                        </label>
                                                        {isSignatureImage(firmaTecnico1) ? (
                                                            <div className="signature-preview-box">
                                                                <img src={firmaTecnico1} alt="Firma tecnico 1" className="signature-preview-img" />
                                                            </div>
                                                        ) : (
                                                            <input className="form-control" value={firmaTecnico1} onChange={(e) => setFirmaTecnico1(e.target.value)} />
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="informes-tecnico-card">
                                                    <div className="informes-tecnico-card-head">
                                                        <span className="informes-tecnico-badge">Tecnico 2</span>
                                                    </div>
                                                    <div>
                                                        <label>Nombre</label>
                                                        <input className="form-control" value={tecnico2} onChange={(e) => setTecnico2(e.target.value)} />
                                                    </div>
                                                    <div>
                                                        <label>
                                                            Firma tecnico 2 {firmaTecnico2 ? <span className="firma-check">✓</span> : null}
                                                        </label>
                                                        {isSignatureImage(firmaTecnico2) ? (
                                                            <div className="signature-preview-box">
                                                                <img src={firmaTecnico2} alt="Firma tecnico 2" className="signature-preview-img" />
                                                            </div>
                                                        ) : (
                                                            <input className="form-control" value={firmaTecnico2} onChange={(e) => setFirmaTecnico2(e.target.value)} />
                                                        )}
                                                    </div>
                                                </div>
                                                {tecnicosAdicionalesActa.map((item, index) => (
                                                    <div className="informes-tecnico-card informes-tecnico-card-extra" key={`tec-extra-${index}`}>
                                                        <div className="informes-tecnico-card-head">
                                                            <span className="informes-tecnico-badge">Tecnico {index + 3}</span>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-link text-danger p-0"
                                                                onClick={() => eliminarTecnicoAdicionalActa(index)}
                                                                title="Quitar tecnico"
                                                            >
                                                                <i className="fas fa-trash-alt" />
                                                            </button>
                                                        </div>
                                                        <div>
                                                            <label>Nombre</label>
                                                            <input
                                                                className="form-control"
                                                                value={item.nombre}
                                                                onChange={(e) => actualizarTecnicoAdicionalActa(index, "nombre", e.target.value)}
                                                            />
                                                        </div>
                                                        <div>
                                                            <label>
                                                                Firma tecnico {index + 3} {item.firma ? <span className="firma-check">✓</span> : null}
                                                            </label>
                                                            {isSignatureImage(item.firma) ? (
                                                                <div className="signature-preview-box">
                                                                    <img src={item.firma} alt={`Firma tecnico ${index + 3}`} className="signature-preview-img" />
                                                                </div>
                                                            ) : (
                                                                <input
                                                                    className="form-control"
                                                                    value={item.firma}
                                                                    onChange={(e) => actualizarTecnicoAdicionalActa(index, "firma", e.target.value)}
                                                                />
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="informes-editor-subsection informes-editor-subsection-recepcion">
                                            <div className="informes-editor-subsection-head">
                                                <div>
                                                    <strong>Recepcion</strong>
                                                    <small>Persona que recibe y confirma el sistema.</small>
                                                </div>
                                            </div>
                                            <div className="informes-tecnicos-grid informes-recepcion-grid">
                                                <div className="informes-tecnico-card informes-recepcion-card">
                                                    <div>
                                                        <label>Recepciona sistema</label>
                                                        <input className="form-control" value={recepcionaNombre} onChange={(e) => setRecepcionaNombre(e.target.value)} />
                                                    </div>
                                                    <div>
                                                        <label>
                                                            Firma recepciona {firmaRecepciona ? <span className="firma-check">✓</span> : null}
                                                        </label>
                                                        {isSignatureImage(firmaRecepciona) ? (
                                                            <div className="signature-preview-box">
                                                                <img src={firmaRecepciona} alt="Firma recepciona" className="signature-preview-img" />
                                                            </div>
                                                        ) : (
                                                            <input className="form-control" value={firmaRecepciona} onChange={(e) => setFirmaRecepciona(e.target.value)} />
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="informes-editor-section">
                                        <h6 className="informes-editor-section-title">Detalle del acta</h6>
                                        <div className="informes-filtros-grid">
                                            <div className="informes-col-span-2">
                                                <label>Los equipos considerados en este sistema, corresponden a</label>
                                                <textarea
                                                    className="form-control"
                                                    rows={3}
                                                    value={equiposConsiderados}
                                                    onChange={(e) => setEquiposConsiderados(e.target.value)}
                                                    placeholder="Listado de equipos considerados en el sistema"
                                                />
                                            </div>
                                            <div className="informes-col-span-2">
                                                <label>En caso de ser traslado, indicar centro de origen</label>
                                                <input
                                                    className="form-control"
                                                    value={centroOrigenTraslado}
                                                    onChange={(e) => setCentroOrigenTraslado(e.target.value)}
                                                    placeholder="Centro de origen (solo traslados)"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="informes-col-span-2 d-flex gap-2 align-items-end">
                                        <button className="btn btn-primary" onClick={handleGuardarActa} disabled={savingActa}>
                                            {savingActa ? "Guardando..." : editandoId ? "Actualizar acta" : "Guardar acta"}
                                        </button>
                                        <button className="btn btn-outline-secondary" onClick={resetFormulario}>
                                            Cerrar
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : null}
                </>
            ) : (subcategoria === "intervencion" || subcategoria === "mantencion" || subcategoria === "retiro" || subcategoria === "levantamiento") ? (
                <>
                    <div className="card informes-centros-tabla">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                                <div>
                                    <h5 className="mb-1">
                                        {subcategoria === "mantencion"
                                            ? "Mantenciones en terreno"
                                            : subcategoria === "retiro"
                                                ? "Retiros en terreno"
                                                : subcategoria === "levantamiento"
                                                    ? "Levantamientos en terreno"
                                                : "Permisos de trabajo"}
                                    </h5>
                                    {subcategoria !== "levantamiento" && (
                                        <p className="text-muted mb-0">
                                            {subcategoria === "mantencion"
                                                ? "Registros creados desde mobile en mantenciones de terreno."
                                                : subcategoria === "retiro"
                                                    ? "Registros de retiro creados desde mobile."
                                                    : "Registros creados desde mobile y web."}
                                        </p>
                                    )}
                                    {subcategoria !== "retiro" && subcategoria !== "levantamiento" ? (
                                        <div className="informes-detalle-toolbar mt-2">
                                            <button
                                                className={`btn btn-sm ${mostrarDetallePermiso ? "btn-primary" : "btn-outline-primary"}`}
                                                onClick={() => setMostrarDetallePermiso((v) => !v)}>
                                                <i className="fas fa-layer-group mr-1" />
                                                {mostrarDetallePermiso ? "Ocultar detalle" : "Detalle"}
                                            </button>
                                            {mostrarDetallePermiso ? (
                                            <>
                                                <button
                                                    className={`btn btn-sm ${detallePermisoTab === "gps" ? "btn-primary" : "btn-outline-primary"}`}
                                                    onClick={() => cambiarTabDetallePermiso("gps")}>
                                                    Puntos GPS
                                                </button>
                                                <button
                                                    className={`btn btn-sm ${detallePermisoTab === "energia" ? "btn-primary" : "btn-outline-primary"}`}
                                                    onClick={() => cambiarTabDetallePermiso("energia")}>
                                                    Energia
                                                </button>
                                            </>
                                            ) : null}
                                        </div>
                                    ) : null}
                                </div>
                                {subcategoria === "intervencion" ? (
                                    <button className="btn btn-primary" onClick={abrirNuevoPermiso}>
                                        <i className="fas fa-plus mr-2" />
                                        Nuevo permiso
                                    </button>
                                ) : null}
                            </div>
                            {loadingCentros || loadingActas ? (
                                <div className="informes-empty">Cargando informacion...</div>
                            ) : !permisosFiltrados.length ? (
                                <div className="informes-empty">
                                    {subcategoria === "mantencion"
                                        ? "No hay mantenciones en terreno para los filtros seleccionados."
                                        : subcategoria === "retiro"
                                            ? "No hay retiros en terreno para los filtros seleccionados."
                                            : subcategoria === "levantamiento"
                                                ? "No hay levantamientos en terreno para los filtros seleccionados."
                                            : "No hay permisos de trabajo para los filtros seleccionados."}
                                </div>
                            ) : (
                                <>
                                    <div className="table-responsive">
                                        <table className="table table-sm table-hover informes-table mb-0">
                                            <thead>
                                                {subcategoria === "levantamiento" ? (
                                                    <tr>
                                                        <th>N</th>
                                                        <th>Correlativo</th>
                                                        <th>Empresa</th>
                                                        <th>Centro</th>
                                                        <th>Codigo Ponton</th>
                                                        <th>Fecha</th>
                                                        <th>Region / Localidad</th>
                                                        <th>Mediciones</th>
                                                        <th>Resumen</th>
                                                        <th>Fotos</th>
                                                        <th>Estado</th>
                                                        <th>Acciones</th>
                                                    </tr>
                                                ) : (
                                                    <tr>
                                                        <th>N</th>
                                                        <th>Correlativo</th>
                                                        <th>Empresa</th>
                                                        <th>Centro</th>
                                                        <th>Codigo Ponton</th>
                                                        <th>Fecha ingreso</th>
                                                        <th>Fecha salida</th>
                                                        <th>Region / Localidad</th>
                                                        <th>Tecnico 1</th>
                                                        <th>Tecnico 2</th>
                                                        <th>Recepciona</th>
                                                        <th>Descripcion</th>
                                                        <th>Acciones</th>
                                                    </tr>
                                                )}
                                            </thead>
                                            <tbody>
                                                {permisosPaginados.map((permiso, index) => {
                                                    const rowKey = permiso.id_permiso_trabajo || permiso.id_mantencion_terreno || permiso.id_retiro_terreno;
                                                    if (subcategoria === "levantamiento") {
                                                        const levantamientoId = permiso.id_levantamiento_terreno || rowKey || index;
                                                        const fotos = getLevantamientoFotos(permiso.fotos);
                                                        const detalleAbierto = String(levantamientoDetalleId) === String(levantamientoId);
                                                        return (
                                                            <React.Fragment key={`levantamiento-${levantamientoId}`}>
                                                                <tr>
                                                                    <td>{(permisoPage - 1) * permisoPageSize + index + 1}</td>
                                                                    <td>{`N${permiso.id_levantamiento_terreno || "-"}`}</td>
                                                                    <td>{permiso.empresa || permiso.cliente || "-"}</td>
                                                                    <td>{permiso.centro || "-"}</td>
                                                                    <td>{permiso.codigo_ponton || "-"}</td>
                                                                    <td>{toDisplayDate(permiso.fecha_levantamiento)}</td>
                                                                    <td>
                                                                        <div>{permiso.region || "-"}</div>
                                                                        <small className="text-muted">{permiso.localidad || "-"}</small>
                                                                    </td>
                                                                    <td>
                                                                        <div className="informes-mediciones-mini">
                                                                            <span>V: {formatMeasureWithUnit(permiso.medicion_voltaje, "V")}</span>
                                                                            <span>A: {formatMeasureWithUnit(permiso.medicion_corriente, "A")}</span>
                                                                            <span>W: {formatMeasureWithUnit(permiso.medicion_potencia, "W")}</span>
                                                                        </div>
                                                                    </td>
                                                                    <td className="equipos-col">{permiso.resumen || permiso.observaciones || "-"}</td>
                                                                    <td>
                                                                        <span className="badge badge-info">{fotos.length}</span>
                                                                    </td>
                                                                    <td>{renderLevantamientoEstadoBadge(permiso.estado)}</td>
                                                                    <td className="acciones-col">
                                                                        <button
                                                                            className={`btn btn-sm ${detalleAbierto ? "btn-primary" : "btn-outline-primary"}`}
                                                                            onClick={() => setLevantamientoDetalleId(detalleAbierto ? null : levantamientoId)}>
                                                                            <i className="fas fa-eye mr-1" />
                                                                            Detalle
                                                                        </button>
                                                                        <button
                                                                            className="btn btn-sm btn-outline-secondary"
                                                                            onClick={() => verLevantamientoPdf(permiso)}>
                                                                            <i className="fas fa-file-pdf mr-1" />
                                                                            Vista previa
                                                                        </button>
                                                                        {normalizeLevantamientoEstado(permiso.estado) === "edicion_solicitada" && (
                                                                            <>
                                                                                <button
                                                                                    className="btn btn-sm btn-outline-success"
                                                                                    onClick={() => handleResolverEdicionLevantamiento(permiso.id_levantamiento_terreno, true)}
                                                                                    title="Aprobar edicion">
                                                                                    <i className="fas fa-check mr-1" />
                                                                                    Aprobar
                                                                                </button>
                                                                                <button
                                                                                    className="btn btn-sm btn-outline-danger"
                                                                                    onClick={() => handleResolverEdicionLevantamiento(permiso.id_levantamiento_terreno, false)}
                                                                                    title="Rechazar edicion">
                                                                                    <i className="fas fa-times mr-1" />
                                                                                    Rechazar
                                                                                </button>
                                                                            </>
                                                                        )}
                                                                        <button
                                                                            className="btn btn-sm btn-outline-danger"
                                                                            onClick={() => handleEliminarLevantamiento(permiso.id_levantamiento_terreno)}>
                                                                            <i className="fas fa-trash-alt" />
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                                {detalleAbierto ? (
                                                                    <tr className="informes-detalle-row">
                                                                        <td colSpan={12}>
                                                                            <div className="informes-detalle-panel">
                                                                                <div className="informes-detalle-grid">
                                                                                    <div className="informes-detalle-full"><small>Resumen</small><strong>{permiso.resumen || "-"}</strong></div>
                                                                                    <div className="informes-detalle-full"><small>Observaciones</small><strong>{permiso.observaciones || "-"}</strong></div>
                                                                                    <div><small>Voltaje</small><strong>{formatMeasureWithUnit(permiso.medicion_voltaje, "V")}</strong></div>
                                                                                    <div><small>Corriente</small><strong>{formatMeasureWithUnit(permiso.medicion_corriente, "A")}</strong></div>
                                                                                    <div><small>Potencia</small><strong>{formatMeasureWithUnit(permiso.medicion_potencia, "W")}</strong></div>
                                                                                </div>
                                                                                {fotos.length ? (
                                                                                    <div className="informes-levantamiento-fotos">
                                                                                        {fotos.map((foto, fotoIndex) => (
                                                                                            <div className="informes-levantamiento-foto" key={`${levantamientoId}-foto-${fotoIndex}`}>
                                                                                                <img src={foto.uri} alt={`Levantamiento ${fotoIndex + 1}`} />
                                                                                                <span>{foto.descripcion || `Foto ${fotoIndex + 1}`}</span>
                                                                                            </div>
                                                                                        ))}
                                                                                    </div>
                                                                                ) : null}
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                ) : null}
                                                            </React.Fragment>
                                                        );
                                                    }
                                                    return (
                                                        <React.Fragment key={`${subcategoria}-${rowKey || index}`}>
                                                            <tr>
                                                                <td>{(permisoPage - 1) * permisoPageSize + index + 1}</td>
                                                                <td>
                                                                    {subcategoria === "mantencion"
                                                                        ? `N${permiso.id_mantencion_terreno || "-"}`
                                                                        : subcategoria === "retiro"
                                                                            ? `N${permiso.id_retiro_terreno || "-"}`
                                                                        : `N${permiso.id_permiso_trabajo || "-"}`}
                                                                </td>
                                                                <td>{permiso.empresa || permiso.cliente || "-"}</td>
                                                                <td>{permiso.centro || "-"}</td>
                                                                <td>{permiso.codigo_ponton || "-"}</td>
                                                                <td>{toDisplayDate(subcategoria === "retiro" ? permiso.fecha_retiro : permiso.fecha_ingreso)}</td>
                                                                <td>{toDisplayDate(permiso.fecha_salida)}</td>
                                                                <td>
                                                                    <div>{permiso.region || "-"}</div>
                                                                    <small className="text-muted">{permiso.localidad || "-"}</small>
                                                                </td>
                                                                <td>{permiso.tecnico_1 || "-"}</td>
                                                                <td>{permiso.tecnico_2 || "-"}</td>
                                                                <td>{permiso.recepciona_nombre || "-"}</td>
                                                                <td className="equipos-col">{permiso.descripcion_trabajo || "-"}</td>
                                                                <td className="acciones-col">
                                                                    {subcategoria === "intervencion" ? (
                                                                        <>
                                                                            <button className="btn btn-sm btn-outline-primary mr-2" onClick={() => handleEditarPermiso(permiso)}>
                                                                                <i className="fas fa-folder-open mr-1" />
                                                                                Abrir
                                                                            </button>
                                                                            <button className="btn btn-sm btn-outline-secondary mr-2" onClick={() => verPermisoPdf(permiso)}>
                                                                                <i className="fas fa-file-pdf mr-1" />
                                                                                Permiso
                                                                            </button>
                                                                            <button className="btn btn-sm btn-outline-danger" onClick={() => handleEliminarPermiso(permiso.id_permiso_trabajo)}>
                                                                                <i className="fas fa-trash-alt" />
                                                                            </button>
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            {subcategoria === "mantencion" ? (
                                                                                <>
                                                                                    <button
                                                                                        className="btn btn-sm btn-outline-primary mr-2"
                                                                                        onClick={() => handleEditarMantencion(permiso)}>
                                                                                        <i className="fas fa-edit mr-1" />
                                                                                        Editar
                                                                                    </button>
                                                                                    <button
                                                                                        className="btn btn-sm btn-outline-secondary mr-2"
                                                                                        onClick={() => verMantencionPdf(permiso)}>
                                                                                        <i className="fas fa-file-pdf mr-1" />
                                                                                        Mantencion
                                                                                    </button>
                                                                                    <button className="btn btn-sm btn-outline-danger" onClick={() => handleEliminarMantencion(permiso.id_mantencion_terreno)}>
                                                                                        <i className="fas fa-trash-alt" />
                                                                                    </button>
                                                                                </>
                                                                            ) : (
                                                                                <>
                                                                                    {String(permiso.estado_edicion || "finalizado").toLowerCase() === "edicion_solicitada" ? (
                                                                                        <>
                                                                                            <button
                                                                                                className="btn btn-sm btn-outline-success mr-2"
                                                                                                onClick={() => handleResolverEdicionRetiro(permiso.id_retiro_terreno, true)}
                                                                                                title="Aprobar edicion">
                                                                                                <i className="fas fa-check mr-1" />
                                                                                                Aprobar
                                                                                            </button>
                                                                                            <button
                                                                                                className="btn btn-sm btn-outline-danger mr-2"
                                                                                                onClick={() => handleResolverEdicionRetiro(permiso.id_retiro_terreno, false)}
                                                                                                title="Rechazar edicion">
                                                                                                <i className="fas fa-times mr-1" />
                                                                                                Rechazar
                                                                                            </button>
                                                                                        </>
                                                                                    ) : null}
                                                                                    <button
                                                                                        className="btn btn-sm btn-outline-primary mr-2"
                                                                                        onClick={() => handleEditarRetiro(permiso)}>
                                                                                        <i className="fas fa-edit mr-1" />
                                                                                        Editar
                                                                                    </button>
                                                                                    <button
                                                                                        className="btn btn-sm btn-outline-secondary mr-2"
                                                                                        onClick={() => verRetiroPdf(permiso)}>
                                                                                        <i className="fas fa-file-pdf mr-1" />
                                                                                        Retiro
                                                                                    </button>
                                                                                    {String(permiso.estado_edicion || "finalizado").toLowerCase() === "edicion_solicitada" ? (
                                                                                        <span className="badge badge-warning mr-2">Edicion solicitada</span>
                                                                                    ) : String(permiso.estado_edicion || "finalizado").toLowerCase() === "edicion_autorizada" ? (
                                                                                        <span className="badge badge-info mr-2">Edicion autorizada</span>
                                                                                    ) : String(permiso.estado_edicion || "finalizado").toLowerCase() === "edicion_rechazada" ? (
                                                                                        <span className="badge badge-danger mr-2">Edicion rechazada</span>
                                                                                    ) : (
                                                                                        <span className="badge badge-success mr-2">Finalizado</span>
                                                                                    )}
                                                                                    <button
                                                                                        className="btn btn-sm btn-outline-danger"
                                                                                        onClick={() => handleEliminarRetiro(permiso.id_retiro_terreno)}>
                                                                                        <i className="fas fa-trash-alt" />
                                                                                    </button>
                                                                                </>
                                                                            )}
                                                                        </>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                            {mostrarDetallePermiso && subcategoria !== "retiro" ? (
                                                                <tr className="informes-detalle-row">
                                                                    <td colSpan={13}>
                                                                        <div className="informes-detalle-panel">
                                                                            {detallePermisoTab === "gps" ? (
                                                                                <div className="informes-detalle-grid">
                                                                                    <div className="informes-detalle-full"><small>Puntos GPS</small><strong>{permiso.puntos_gps || "-"}</strong></div>
                                                                                </div>
                                                                            ) : null}
                                                                            {detallePermisoTab === "energia" ? (
                                                                                <div className="informes-detalle-grid">
                                                                                    <div><small>Fase / Neutro</small><strong>{formatMeasureWithUnit(permiso.medicion_fase_neutro, "V")}</strong></div>
                                                                                    <div><small>Neutro / Tierra</small><strong>{formatMeasureWithUnit(permiso.medicion_neutro_tierra, "V")}</strong></div>
                                                                                    <div><small>Hertz</small><strong>{formatMeasureWithUnit(permiso.hertz, "Hz")}</strong></div>
                                                                                </div>
                                                                            ) : null}
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            ) : null}
                                                        </React.Fragment>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="informes-pagination-bar">
                                        <div className="informes-pagination-left">
                                            <span className="informes-pagination-label">
                                                <i className="fas fa-list-ul" /> Ver
                                            </span>
                                            <select
                                                className="form-control form-control-sm informes-page-size-select"
                                                value={permisoPageSize}
                                                onChange={(e) => setPermisoPageSize(Number(e.target.value) || 10)}>
                                                <option value={10}>10</option>
                                                <option value={15}>15</option>
                                                <option value={30}>30</option>
                                            </select>
                                            <span className="informes-pagination-label">registros</span>
                                        </div>
                                        <small className="informes-pagination-count">
                                            Mostrando {(permisoPage - 1) * permisoPageSize + 1}-{Math.min(permisoPage * permisoPageSize, permisosFiltrados.length)} de {permisosFiltrados.length}
                                        </small>
                                        <div className="informes-pagination-right">
                                            <button
                                                className="btn btn-sm btn-outline-secondary informes-page-btn"
                                                disabled={permisoPage <= 1}
                                                onClick={() => setPermisoPage((p) => Math.max(1, p - 1))}>
                                                <i className="fas fa-chevron-left" /> Anterior
                                            </button>
                                            <span className="informes-page-indicator">
                                                Pagina {permisoPage} / {totalPermisoPages}
                                            </span>
                                            <button
                                                className="btn btn-sm btn-outline-secondary informes-page-btn"
                                                disabled={permisoPage >= totalPermisoPages}
                                                onClick={() => setPermisoPage((p) => Math.min(totalPermisoPages, p + 1))}>
                                                Siguiente <i className="fas fa-chevron-right" />
                                            </button>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {mostrarEditorPermiso ? (subcategoria === "retiro" ? renderRetiroEditor() : renderPermisoEditor()) : null}
                </>
            ) : (
                <div className="card informes-centros-tabla">
                    <div className="card-body">
                        <div className="informes-empty">
                            Esta subcategoria aun no esta implementada. Continuamos con {SUBCATEGORIAS.find((s) => s.value === subcategoria)?.label}.
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
}

export default InformesCentros;
